import { z } from "zod";

// ============================================
// ENUMS & CONSTANTS
// ============================================

export const UserRole = {
  CUSTOMER: "customer",
  EMPLOYEE: "employee",
  CASHIER: "cashier",
  ADMIN: "admin",
} as const;

export const VehicleType = {
  SEDAN: "sedan", // Binek
  SUV: "suv", // SUV
  LARGE: "large", // Büyük Araç
} as const;

export const WashStatus = {
  PENDING: "pending", // Bekliyor (randevu alındı ama onay bekliyor)
  CONFIRMED: "confirmed", // Onaylandı
  QUEUED: "queued", // Sıraya Alındı
  IN_PROGRESS: "in_progress", // Başladı
  COMPLETED: "completed", // Bitti
  CANCELLED: "cancelled", // İptal
} as const;

export const CouponType = {
  PERCENTAGE: "percentage", // Yüzde indirim
  FREE_SERVICE: "free_service", // Bedava hizmet
} as const;

export const ExpenseCategory = {
  REVENUE: "revenue", // Gelir
  MATERIALS: "materials", // Malzeme
  LABOR: "labor", // İşçilik
  RENT: "rent", // Kira
  SALARY: "salary", // Maaş
  INSURANCE: "insurance", // Sigorta
  BONUS: "bonus", // Prim
  OTHER: "other", // Diğer
} as const;

export const PaymentStatus = {
  PENDING: "pending", // Bekliyor
  PAID: "paid", // Ödendi
  SUBSCRIPTION: "subscription", // Abonelik kullanıldı
} as const;

export const PaymentMethod = {
  CASH: "cash", // Nakit
  CARD: "card", // Kredi Kartı
  TRANSFER: "transfer", // Havale/EFT
  CORPORATE_CURRENT: "corporate_current", // Şirket Cari Hesap
} as const;

export const CleaningFrequency = {
  DAILY: "daily", // Günlük
  WEEKLY: "weekly", // Haftalık
  BIWEEKLY: "biweekly", // 2 Haftada Bir
  MONTHLY: "monthly", // Aylık
  CUSTOM: "custom", // Özel
} as const;

export const ServiceCategory = {
  EXTERIOR: "exterior", // Dış Yıkama
  INTERIOR: "interior", // İç Temizlik
  DETAILING: "detailing", // Detaylı Bakım
  PACKAGE: "package", // Paket Hizmetler
  EXTRA: "extra", // Ekstra Hizmetler
} as const;

export const QuoteStatus = {
  PENDING: "pending", // Bekliyor
  REVIEWED: "reviewed", // İncelendi
  QUOTED: "quoted", // Teklif Verildi
  SENT: "sent", // Gönderildi
  ACCEPTED: "accepted", // Kabul Edildi
  REJECTED: "rejected", // Reddedildi
} as const;

export const NotificationType = {
  APPOINTMENT_CREATED: "appointment_created", // Yeni randevu talebi
  APPOINTMENT_CONFIRMED: "appointment_confirmed", // Randevu onaylandı
  APPOINTMENT_CANCELLED: "appointment_cancelled", // Randevu iptal edildi
  APPOINTMENT_RESCHEDULED: "appointment_rescheduled", // Randevu değiştirildi
  WASH_QUEUED: "wash_queued", // Araç sıraya alındı
  WASH_STARTED: "wash_started", // Yıkama başladı
  WASH_COMPLETED: "wash_completed", // Araç hazır
  PAYMENT_RECEIVED: "payment_received", // Ödeme alındı
  GIFT_RECEIVED: "gift_received", // Hediye kazanıldı
} as const;

// ============================================
// NOTIFICATION TEMPLATE SCHEMA
// ============================================

export const NotificationIcon = {
  BELL: "bell",
  CALENDAR: "calendar",
  CAR: "car",
  CHECK: "check",
  X: "x",
  CLOCK: "clock",
  CREDIT_CARD: "credit-card",
  GIFT: "gift",
  INFO: "info",
  ALERT: "alert-triangle",
} as const;

export const NotificationPriority = {
  LOW: "low",
  NORMAL: "normal",
  HIGH: "high",
} as const;

export const notificationTemplateSchema = z.object({
  id: z.string(),
  type: z.enum([
    NotificationType.APPOINTMENT_CREATED,
    NotificationType.APPOINTMENT_CONFIRMED,
    NotificationType.APPOINTMENT_CANCELLED,
    NotificationType.APPOINTMENT_RESCHEDULED,
    NotificationType.WASH_QUEUED,
    NotificationType.WASH_STARTED,
    NotificationType.WASH_COMPLETED,
    NotificationType.PAYMENT_RECEIVED,
    NotificationType.GIFT_RECEIVED,
  ]),
  title: z.string(),
  body: z.string(),
  targetRole: z.enum(["customer", "employee", "cashier", "admin", "all"]),
  enabled: z.boolean().default(true),
  version: z.number().default(1),
  updatedBy: z.string().optional(),
  soundId: z.string().optional(),
  icon: z.enum([
    NotificationIcon.BELL,
    NotificationIcon.CALENDAR,
    NotificationIcon.CAR,
    NotificationIcon.CHECK,
    NotificationIcon.X,
    NotificationIcon.CLOCK,
    NotificationIcon.CREDIT_CARD,
    NotificationIcon.GIFT,
    NotificationIcon.INFO,
    NotificationIcon.ALERT,
  ]).default(NotificationIcon.BELL).optional(),
  priority: z.enum([
    NotificationPriority.LOW,
    NotificationPriority.NORMAL,
    NotificationPriority.HIGH,
  ]).default(NotificationPriority.NORMAL).optional(),
  vibrate: z.boolean().default(true).optional(),
  persistUntilRead: z.boolean().default(false).optional(),
  availableVariables: z.array(z.string()).optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertNotificationTemplateSchema = notificationTemplateSchema.omit({ 
  id: true, 
  version: true,
  createdAt: true, 
  updatedAt: true 
});

export type NotificationTemplate = z.infer<typeof notificationTemplateSchema>;
export type InsertNotificationTemplate = z.infer<typeof insertNotificationTemplateSchema>;

// ============================================
// NOTIFICATION SOUNDS SCHEMA (Firebase Collection)
// ============================================

export const notificationSoundSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertNotificationSoundSchema = notificationSoundSchema.omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});

export type NotificationSound = z.infer<typeof notificationSoundSchema>;
export type InsertNotificationSound = z.infer<typeof insertNotificationSoundSchema>;

// ============================================
// NOTIFICATION TEMPLATE VERSION HISTORY SCHEMA
// ============================================

export const templateVersionSchema = z.object({
  id: z.string(),
  templateId: z.string(), // Reference to notification template
  type: z.string(), // Template type (for reference)
  version: z.number(), // Version number
  title: z.string(), // Historical title
  body: z.string(), // Historical body
  targetRole: z.enum(["customer", "employee", "cashier", "admin", "all"]),
  enabled: z.boolean(),
  updatedBy: z.string(), // Admin ID who made this change
  createdAt: z.number(), // When this version was created
});

export const insertTemplateVersionSchema = templateVersionSchema.omit({ 
  id: true, 
  createdAt: true 
});

export type TemplateVersion = z.infer<typeof templateVersionSchema>;
export type InsertTemplateVersion = z.infer<typeof insertTemplateVersionSchema>;

// ============================================
// USER NOTIFICATION SCHEMA (Firestore subcollection)
// ============================================

export const UserNotificationType = {
  APPOINTMENT: "appointment",
  WASH: "wash",
  GENERAL: "general",
} as const;

export const userNotificationSchema = z.object({
  id: z.string(),
  userId: z.string(), // Owner of the notification
  title: z.string(),
  body: z.string(),
  type: z.enum([
    UserNotificationType.APPOINTMENT,
    UserNotificationType.WASH,
    UserNotificationType.GENERAL,
  ]),
  read: z.boolean().default(false),
  createdAt: z.number(),
  data: z.object({
    appointmentId: z.string().optional(),
    washId: z.string().optional(),
    link: z.string().optional(),
  }).optional(),
});

export const insertUserNotificationSchema = userNotificationSchema.omit({ 
  id: true,
  userId: true,
  createdAt: true,
});

export type UserNotification = z.infer<typeof userNotificationSchema>;
export type InsertUserNotification = z.infer<typeof insertUserNotificationSchema>;

// ============================================
// SITE SETTINGS SCHEMA
// ============================================

export const siteSettingsSchema = z.object({
  id: z.string(),
  // Business Information
  businessName: z.string().min(1, "İşletme adı gerekli").default("TemizFilo"),
  phone: z.string().min(1, "Telefon gerekli").default("+90 555 123 45 67"),
  email: z.string().email("Geçerli email adresi gerekli").default("info@temizfilo.com"),
  whatsapp: z.string().min(1, "WhatsApp numarası gerekli").default("+905551234567"),
  address: z.string().min(1, "Adres gerekli").default("Örnek Mahalle, Araç Yıkama Cad. No:123, Kadıköy / İstanbul"),
  googleMapsUrl: z.string().url().optional(),
  
  // Social Media Links
  facebookUrl: z.string().url().optional(),
  instagramUrl: z.string().url().optional(),
  twitterUrl: z.string().url().optional(),
  linkedinUrl: z.string().url().optional(),
  youtubeUrl: z.string().url().optional(),
  
  // SEO Settings
  seoTitle: z.string().optional(), // Meta title (max 60 chars)
  seoDescription: z.string().optional(), // Meta description (max 160 chars)
  seoKeywords: z.string().optional(), // Comma-separated keywords
  googleAnalyticsId: z.string().optional(), // GA4 Measurement ID (G-XXXXXXXXXX)
  googleTagManagerId: z.string().optional(), // GTM ID (GTM-XXXXXXX)
  facebookPixelId: z.string().optional(), // Facebook Pixel ID
  
  // Open Graph (Social Sharing)
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
  ogImageUrl: z.string().url().optional(),
  
  // Content Pages
  aboutPageHtml: z.string().optional(), // HTML content for About page
  privacyPolicyHtml: z.string().optional(), // HTML content for Privacy Policy
  termsOfServiceHtml: z.string().optional(), // HTML content for Terms
  footerText: z.string().optional(), // Footer copyright text
  
  // Business Hours Display
  showBusinessHours: z.boolean().default(true),
  businessHoursText: z.string().optional(), // Custom hours text if not using default
  
  updatedAt: z.number(),
  updatedBy: z.string(), // Admin ID
});

export const insertSiteSettingsSchema = siteSettingsSchema.omit({ id: true, updatedAt: true });
export type SiteSettings = z.infer<typeof siteSettingsSchema>;
export type InsertSiteSettings = z.infer<typeof insertSiteSettingsSchema>;

// ============================================
// USER SCHEMA
// ============================================

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  photoURL: z.string().nullable(),
  phone: z.string().nullable().optional(), // Telefon numarası (opsiyonel)
  phoneVerified: z.boolean().default(false).optional(), // Telefon doğrulandı mı?
  role: z.enum([UserRole.CUSTOMER, UserRole.EMPLOYEE, UserRole.CASHIER, UserRole.ADMIN]),
  companyId: z.string().nullable().optional(), // Kurumsal şirket çalışanı ise şirket ID
  companyName: z.string().nullable().optional(), // Kullanıcının kendi şirket adı (opsiyonel)
  fcmToken: z.string().nullable().optional(), // Firebase Cloud Messaging token for push notifications
  createdAt: z.number(), // Timestamp
});

export const insertUserSchema = userSchema.omit({ id: true, createdAt: true });
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = z.infer<typeof userSchema>;

// ============================================
// VEHICLE SCHEMA
// ============================================

export const vehicleSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(), // Sahibi (misafir kullanıcılar için null)
  companyId: z.string().nullable().optional(), // Şirket filosu için (kurumsal araçlar)
  plate: z.string().min(1, "Plaka zorunludur"),
  brand: z.string().min(1, "Marka zorunludur"),
  model: z.string().min(1, "Model zorunludur"),
  color: z.string().min(1, "Renk zorunludur"),
  type: z.enum([VehicleType.SEDAN, VehicleType.SUV, VehicleType.LARGE]),
  createdAt: z.number(),
});

export const insertVehicleSchema = vehicleSchema.omit({ id: true, createdAt: true });
export type InsertVehicle = z.infer<typeof insertVehicleSchema>;
export type Vehicle = z.infer<typeof vehicleSchema>;

// ============================================
// SERVICE SCHEMA
// ============================================

export const servicePricingSchema = z.object({
  sedan: z.number().min(0),
  suv: z.number().min(0),
  large: z.number().min(0),
});

export const serviceSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Hizmet adı zorunludur"),
  description: z.string().optional(), // Kısa açıklama
  longDescription: z.string().optional(), // Detaylı açıklama (detay sayfası için)
  imageUrl: z.string().optional(), // Hizmet görseli URL
  category: z.enum([
    ServiceCategory.EXTERIOR,
    ServiceCategory.INTERIOR,
    ServiceCategory.DETAILING,
    ServiceCategory.PACKAGE,
    ServiceCategory.EXTRA,
  ]).default(ServiceCategory.EXTERIOR),
  durationMinutes: z.number().min(1, "Süre zorunludur"), // Dakika cinsinden
  pricing: servicePricingSchema,
  active: z.boolean().default(true),
  isFeatured: z.boolean().default(false), // Anasayfada öne çıkan hizmetler
  isPopular: z.boolean().default(false), // Rezervasyon sayfasında popüler hizmetler (max 12)
  createdAt: z.number(),
});

export const insertServiceSchema = serviceSchema.omit({ id: true, createdAt: true });
export type InsertService = z.infer<typeof insertServiceSchema>;
export type Service = z.infer<typeof serviceSchema>;

// ============================================
// APPOINTMENT SCHEMA
// ============================================

// Reservation source types
export const AppointmentSource = {
  ONLINE: 'online',     // Web sitesinden yapılan online rezervasyonlar
  WALK_IN: 'walk-in',   // Direkt gelen müşteriler (çalışan tarafından eklenen)
  PHONE: 'phone',       // Telefon ile yapılan rezervasyonlar
} as const;

export const appointmentSchema = z.object({
  id: z.string(),
  reservationCode: z.string(), // 4 karakterli kod
  customerId: z.string().nullable(), // Misafir kullanıcılar için null
  customerName: z.string().min(1, "Müşteri adı zorunludur"), // Müşteri adı (misafir için zorunlu)
  customerPhone: z.string().min(1, "Telefon numarası zorunludur"), // Misafir kullanıcılar için iletişim
  vehicleId: z.string(),
  serviceIds: z.array(z.string()).min(1, "En az bir hizmet seçilmelidir"), // Birden fazla hizmet
  scheduledDate: z.string(), // YYYY-MM-DD
  scheduledTime: z.string(), // HH:mm
  status: z.enum([
    WashStatus.PENDING,
    WashStatus.CONFIRMED,
    WashStatus.QUEUED,
    WashStatus.IN_PROGRESS,
    WashStatus.COMPLETED,
    WashStatus.CANCELLED,
    "rejected", // Employee tarafından reddedilen randevular
    "pending_hold", // Hold durumunda bekliyor
    "waitlist", // Bekleme listesinde
  ]),
  totalDuration: z.number().min(1), // Toplam süre (dakika)
  totalAmount: z.number().min(0), // Toplam tutar
  estimatedCompletionTime: z.number().nullable(), // Tahmini teslim zamanı (timestamp)
  source: z.enum([AppointmentSource.ONLINE, AppointmentSource.WALK_IN, AppointmentSource.PHONE]).default(AppointmentSource.ONLINE), // Rezervasyon kaynağı
  createdAt: z.number(),
  confirmedAt: z.number().nullable(), // Onaylanma zamanı
  confirmedBy: z.string().nullable().optional(), // Kim onayladı? (employee ID veya "auto_system")
  rejectedAt: z.number().nullable().optional(), // Reddedilme zamanı
  rejectionReason: z.string().nullable().optional(), // Red nedeni (time_conflict, capacity_full, manual_reject, etc.)
  suggestedAlternatives: z.array(z.object({
    date: z.string(),
    time: z.string(),
  })).optional(), // Alternatif saat önerileri
  couponCode: z.string().nullable(), // Kullanılan kupon
  paymentStatus: z.enum([PaymentStatus.PENDING, PaymentStatus.PAID]).default(PaymentStatus.PENDING), // Ödeme durumu
  paymentMethod: z.enum([PaymentMethod.CASH, PaymentMethod.CARD, PaymentMethod.TRANSFER, PaymentMethod.CORPORATE_CURRENT]).nullable(), // Ödeme yöntemi
  paidAt: z.number().nullable(), // Ödeme alınma zamanı
  bulkRequestId: z.string().nullable().optional(), // Toplu randevu grubu için benzersiz ID
  priority: z.number().default(0).optional(), // Priority skoru (corporate=3, registered=2, guest=1)
  holdExpiresAt: z.number().nullable().optional(), // Hold süresi dolma zamanı (timestamp)
  subscriptionId: z.string().nullable().optional(), // Kullanılan abonelik ID
  isSubscriptionUsed: z.boolean().default(false), // Abonelik kullanıldı mı?
});

export const insertAppointmentSchema = appointmentSchema.omit({
  id: true,
  reservationCode: true,
  createdAt: true,
  confirmedAt: true,
});
export type InsertAppointment = z.infer<typeof insertAppointmentSchema>;
export type Appointment = z.infer<typeof appointmentSchema>;

// ============================================
// TIME SLOTS SCHEMA (Kapasite Yönetimi)
// ============================================

export const timeSlotHoldSchema = z.object({
  appointmentId: z.string(), // Bu slotu tutan randevu ID
  customerId: z.string().nullable(), // Müşteri ID (guest için null)
  expiresAt: z.number(), // Hold süresi dolma zamanı (timestamp)
  priority: z.number(), // Priority skoru (corporate=3, registered=2, guest=1)
  isGuest: z.boolean(), // Guest kullanıcı mı?
});

export const timeSlotSchema = z.object({
  id: z.string(), // Format: "YYYY-MM-DD_HH:mm" (örn: "2025-11-06_14:00")
  date: z.string(), // YYYY-MM-DD
  time: z.string(), // HH:mm
  capacity: z.number().min(1).default(3), // Aynı anda max kaç araç (varsayılan: 3)
  reserved: z.number().min(0).default(0), // Onaylanmış randevu sayısı
  available: z.number().min(0).default(3), // Boş slot sayısı (capacity - reserved)
  holds: z.array(timeSlotHoldSchema).default([]), // Hold edilen slotlar (15 dk TTL)
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertTimeSlotSchema = timeSlotSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTimeSlot = z.infer<typeof insertTimeSlotSchema>;
export type TimeSlot = z.infer<typeof timeSlotSchema>;
export type TimeSlotHold = z.infer<typeof timeSlotHoldSchema>;

// ============================================
// WASH (İşlem) SCHEMA
// ============================================

export const photoSchema = z.object({
  url: z.string(),
  type: z.enum(["before", "after"]),
  uploadedAt: z.number(),
});

export const washSchema = z.object({
  id: z.string(),
  appointmentId: z.string().nullable(), // Randevulu ise
  reservationCode: z.string(), // 4 karakter kod (randevudan kopyalanır)
  customerId: z.string().nullable(), // Misafir kullanıcılar için null
  customerName: z.string().min(1, "Müşteri adı zorunludur"), // Müşteri adı (misafir için zorunlu)
  customerPhone: z.string().min(1, "Telefon numarası zorunludur"), // Misafir kullanıcılar için iletişim
  companyId: z.string().nullable().optional(), // Kurumsal müşteri ise şirket ID
  vehicleId: z.string(),
  vehiclePlate: z.string().optional(), // Denormalized: Araç plakası (sorguları azaltmak için)
  serviceIds: z.array(z.string()).min(1, "En az bir hizmet seçilmelidir"), // Birden fazla hizmet
  employeeId: z.string().nullable(), // İşi yapan çalışan
  status: z.enum([
    WashStatus.PENDING,
    WashStatus.CONFIRMED,
    WashStatus.QUEUED,
    WashStatus.IN_PROGRESS,
    WashStatus.COMPLETED,
    WashStatus.CANCELLED,
  ]),
  scheduledDate: z.string(),
  scheduledTime: z.string(),
  startedAt: z.number().nullable(),
  completedAt: z.number().nullable(),
  estimatedCompletionTime: z.number().nullable(), // Tahmini bitiş zamanı (timestamp)
  totalDuration: z.number().min(1), // Toplam süre (dakika)
  photos: z.array(photoSchema).default([]),
  paymentStatus: z.enum([PaymentStatus.PENDING, PaymentStatus.PAID, PaymentStatus.SUBSCRIPTION]),
  paymentMethod: z.enum([PaymentMethod.CASH, PaymentMethod.CARD, PaymentMethod.TRANSFER, PaymentMethod.CORPORATE_CURRENT]).nullable(),
  paidAt: z.number().nullable(),
  totalAmount: z.number(),
  couponCode: z.string().nullable(),
  discountAmount: z.number().default(0),
  finalAmount: z.number(),
  reviewId: z.string().nullable(), // Varsa müşterinin verdiği review
  notes: z.string().optional(),
  source: z.enum([AppointmentSource.ONLINE, AppointmentSource.WALK_IN, AppointmentSource.PHONE]).default(AppointmentSource.WALK_IN), // Kaynak: online randevu mu walk-in mi
  isSubscriptionUsed: z.boolean().default(false), // Abonelik kullanıldı mı?
  subscriptionId: z.string().nullable().optional(), // Kullanılan abonelik ID
  createdAt: z.number(),
});

export const insertWashSchema = washSchema.omit({ id: true, createdAt: true });
export type InsertWash = z.infer<typeof insertWashSchema>;
export type Wash = z.infer<typeof washSchema>;

// ============================================
// EMPLOYEE SCHEMA
// ============================================

export const EmploymentStatus = {
  ACTIVE: "active", // Çalışıyor
  ON_LEAVE: "on_leave", // İzinli
  TERMINATED: "terminated", // İşten Ayrıldı
} as const;

export const PaymentType = {
  SALARY: "salary", // Maaş
  BONUS: "bonus", // Prim
  INSURANCE: "insurance", // Sigorta
} as const;

export const employeeProfileSchema = z.object({
  id: z.string(),
  userId: z.string().optional(), // User ID'si (opsiyonel, manuel giriş için)
  name: z.string().min(1, "İsim zorunludur"),
  phone: z.string().optional(), // Telefon
  hireDate: z.string().optional(), // İşe giriş tarihi (YYYY-MM-DD)
  monthlySalary: z.number().min(0).default(0), // Aylık maaş
  monthlyInsurance: z.number().min(0).default(0), // Aylık sigorta bedeli
  status: z.enum([EmploymentStatus.ACTIVE, EmploymentStatus.ON_LEAVE, EmploymentStatus.TERMINATED]).default(EmploymentStatus.ACTIVE),
  notes: z.string().optional(), // Notlar
  createdAt: z.number(),
});

export const insertEmployeeProfileSchema = employeeProfileSchema.omit({
  id: true,
  createdAt: true,
});
export type InsertEmployeeProfile = z.infer<typeof insertEmployeeProfileSchema>;
export type EmployeeProfile = z.infer<typeof employeeProfileSchema>;

// ============================================
// EMPLOYEE PAYMENT SCHEMA
// ============================================

export const employeePaymentSchema = z.object({
  id: z.string(),
  employeeId: z.string(), // EmployeeProfile ID
  employeeName: z.string(), // Çalışan adı (denormalized)
  type: z.enum([PaymentType.SALARY, PaymentType.BONUS, PaymentType.INSURANCE]),
  amount: z.number().min(0),
  month: z.string(), // YYYY-MM formatında ay
  paymentDate: z.string(), // YYYY-MM-DD formatında ödeme tarihi
  notes: z.string().optional(),
  expenseId: z.string().optional(), // Otomatik oluşturulan expense kaydı
  createdBy: z.string(), // Admin user ID
  createdAt: z.number(),
});

export const insertEmployeePaymentSchema = employeePaymentSchema.omit({
  id: true,
  createdAt: true,
  expenseId: true, // Cloud Function tarafından eklenir
});
export type InsertEmployeePayment = z.infer<typeof insertEmployeePaymentSchema>;
export type EmployeePayment = z.infer<typeof employeePaymentSchema>;

// ============================================
// COUPON SCHEMA
// ============================================

export const couponSchema = z.object({
  id: z.string(),
  code: z.string().min(1, "Kupon kodu zorunludur"),
  type: z.enum([CouponType.PERCENTAGE, CouponType.FREE_SERVICE]),
  discountPercentage: z.number().min(0).max(100).nullable(), // Percentage için
  freeServiceId: z.string().nullable(), // Free service için
  validFrom: z.number(), // Timestamp
  validUntil: z.number(), // Timestamp
  maxUses: z.number().nullable(), // null = sınırsız (toplam kullanım limiti)
  usageLimitPerUser: z.number().optional().nullable().default(null), // null = sınırsız (her hesap için kullanım limiti)
  currentUses: z.number().default(0),
  active: z.boolean().default(true),
  createdAt: z.number(),
});

export const insertCouponSchema = couponSchema.omit({
  id: true,
  currentUses: true,
  createdAt: true,
});
export type InsertCoupon = z.infer<typeof insertCouponSchema>;
export type Coupon = z.infer<typeof couponSchema>;

// ============================================
// COUPON USAGE LOG SCHEMA (Kupon Kullanim Gecmisi)
// ============================================

export const couponUsageLogSchema = z.object({
  id: z.string(),
  couponId: z.string(),
  couponCode: z.string(),
  customerId: z.string().nullable(), // null = guest
  customerName: z.string(),
  customerPhone: z.string(),
  appointmentId: z.string().nullable(), // Randevu ID
  washId: z.string().nullable(), // Yikama ID (randevu tamamlandiginda)
  discountType: z.enum([CouponType.PERCENTAGE, CouponType.FREE_SERVICE]),
  discountAmount: z.number().default(0), // Indirim miktari (TL)
  discountPercentage: z.number().nullable(), // Indirim yuzdesi
  freeServiceId: z.string().nullable(), // Bedava hizmet ID
  freeServiceName: z.string().nullable(), // Bedava hizmet adi
  usedAt: z.number(), // Kullanim tarihi (timestamp)
});

export const insertCouponUsageLogSchema = couponUsageLogSchema.omit({
  id: true,
});
export type InsertCouponUsageLog = z.infer<typeof insertCouponUsageLogSchema>;
export type CouponUsageLog = z.infer<typeof couponUsageLogSchema>;

// ============================================
// EXPENSE SCHEMA
// ============================================

export const expenseSchema = z.object({
  id: z.string(),
  category: z.enum([
    ExpenseCategory.REVENUE,
    ExpenseCategory.MATERIALS,
    ExpenseCategory.LABOR,
    ExpenseCategory.RENT,
    ExpenseCategory.SALARY,
    ExpenseCategory.INSURANCE,
    ExpenseCategory.BONUS,
    ExpenseCategory.OTHER,
  ]),
  description: z.string().min(1, "Açıklama zorunludur"),
  amount: z.number().min(0),
  date: z.string(), // YYYY-MM-DD
  relatedEmployeeId: z.string().optional(), // İlgili çalışan ID (maaş/prim/sigorta için)
  createdBy: z.string(), // Admin user ID
  createdAt: z.number(),
});

export const insertExpenseSchema = expenseSchema.omit({ id: true, createdAt: true });
export type InsertExpense = z.infer<typeof insertExpenseSchema>;
export type Expense = z.infer<typeof expenseSchema>;

// ============================================
// FIXED EXPENSE SCHEMA (Sabit Giderler)
// ============================================

export const fixedExpenseSchema = z.object({
  id: z.string(),
  category: z.enum([
    ExpenseCategory.MATERIALS,
    ExpenseCategory.LABOR,
    ExpenseCategory.RENT,
    ExpenseCategory.SALARY,
    ExpenseCategory.INSURANCE,
    ExpenseCategory.BONUS,
    ExpenseCategory.OTHER,
  ]),
  description: z.string().min(1, "Açıklama zorunludur"),
  amount: z.number().min(0),
  createdBy: z.string(),
  createdAt: z.number(),
});

export const insertFixedExpenseSchema = fixedExpenseSchema.omit({ id: true, createdAt: true });
export type InsertFixedExpense = z.infer<typeof insertFixedExpenseSchema>;
export type FixedExpense = z.infer<typeof fixedExpenseSchema>;

// ============================================
// REVIEW SCHEMA
// ============================================

export const reviewSchema = z.object({
  id: z.string(),
  washId: z.string(),
  customerId: z.string(),
  rating: z.number().min(1).max(5), // 5 üzerinden puan
  comment: z.string().optional(),
  approved: z.boolean().default(false), // Admin onayı
  public: z.boolean().default(false), // Public görünür mü
  createdAt: z.number(),
});

export const insertReviewSchema = reviewSchema.omit({
  id: true,
  approved: true,
  public: true,
  createdAt: true,
});
export type InsertReview = z.infer<typeof insertReviewSchema>;
export type Review = z.infer<typeof reviewSchema>;

// ============================================
// CORPORATE QUOTE SCHEMA
// ============================================

export const fleetCompositionSchema = z.object({
  sedan: z.number().min(0).default(0),
  suv: z.number().min(0).default(0),
  large: z.number().min(0).default(0),
});

// Company Status - CRM durumları
export const CompanyStatus = {
  LEAD: "lead", // Potansiyel müşteri
  QUOTE_SENT: "quoteSent", // Teklif gönderildi
  AWAITING_RESPONSE: "awaitingResponse", // Yanıt bekleniyor
  NEGOTIATING: "negotiating", // Görüşme aşamasında
  DEAL_CLOSED: "dealClosed", // Anlaşma yapıldı
  INACTIVE: "inactive", // Pasif
} as const;

export const CorporatePaymentType = {
  CARI_HESAP: "cariHesap", // Cari hesap - Toplu faturalama
  PESIN: "pesin", // Peşin - Her yıkamada ödeme (özel fiyatla)
} as const;

// Corporate Company - Firma bilgileri (opsiyonel, quote'tan bağımsız veya bağlı olabilir)
export const corporateCompanySchema = z.object({
  id: z.string(),
  companyName: z.string().min(1, "Şirket adı zorunludur"),
  taxNumber: z.string().optional(), // Vergi numarası
  industry: z.string().optional(), // Sektör
  address: z.string().optional(), // Adres
  city: z.string().optional(), // Şehir
  postalCode: z.string().optional(), // Posta kodu
  contactName: z.string().min(1, "Yetkili adı zorunludur"),
  contactEmail: z.string().email("Geçerli bir email girin"),
  contactPhone: z.string().min(1, "Telefon zorunludur"),
  contactTitle: z.string().optional(), // Yetkili ünvanı
  notes: z.string().optional(), // Firma hakkında notlar
  status: z.string().default(CompanyStatus.LEAD), // CRM durumu
  paymentType: z.string().default(CorporatePaymentType.CARI_HESAP), // Ödeme tipi
  // Firma yetkilileri - bu mailler firmaya bağlı kullanıcılar olarak kabul edilir
  authorizedEmails: z.array(z.string().email()).optional().default([]), // Yetkili e-posta adresleri
  authorizedUserIds: z.array(z.string()).optional().default([]), // Kayıtlı kullanıcı ID'leri
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCorporateCompanySchema = corporateCompanySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CorporateCompany = z.infer<typeof corporateCompanySchema>;
export type InsertCorporateCompany = z.infer<typeof insertCorporateCompanySchema>;

// ============================================
// CORPORATE PAYMENT (CARİ ÖDEME) SCHEMA
// ============================================

export const corporatePaymentSchema = z.object({
  id: z.string(),
  companyId: z.string(), // CorporateCompany referansı
  companyName: z.string(), // Denormalize - hızlı erişim için
  amount: z.number().min(0), // Ödeme tutarı (TL)
  paymentMonth: z.string(), // Hangi aya ait: "2025-01", "2025-02" vb.
  paymentDate: z.number(), // Ödemenin yapıldığı tarih (timestamp)
  invoiceUrl: z.string().optional(), // Fatura dosyası URL'i (görsel veya PDF)
  invoiceFileName: z.string().optional(), // Yüklenen dosya adı
  notes: z.string().optional(), // Ödeme notu
  recordedBy: z.string().optional(), // Kaydeden admin/kasiyer ID
  recordedByName: z.string().optional(), // Kaydeden kişi adı
  createdAt: z.number(),
});

export const insertCorporatePaymentSchema = corporatePaymentSchema.omit({
  id: true,
  createdAt: true,
});
export type CorporatePayment = z.infer<typeof corporatePaymentSchema>;
export type InsertCorporatePayment = z.infer<typeof insertCorporatePaymentSchema>;

// ============================================
// CORPORATE DEBT (CARİ BORÇ) SCHEMA - Yıkamalardan oluşan borçlar
// ============================================

export const corporateDebtSchema = z.object({
  id: z.string(),
  companyId: z.string(), // CorporateCompany referansı
  companyName: z.string(), // Denormalize - hızlı erişim için
  washId: z.string(), // Wash referansı
  vehicleId: z.string(), // Araç ID
  licensePlate: z.string(), // Plaka
  amount: z.number().min(0), // Borç tutarı (yıkama ücreti)
  debtMonth: z.string(), // Hangi aya ait: "2025-01", "2025-02" vb.
  washDate: z.number(), // Yıkama tarihi
  isPaid: z.boolean().default(false), // Ödendi mi?
  paidAt: z.number().optional(), // Ödeme tarihi
  paymentId: z.string().optional(), // İlişkili ödeme ID
  serviceNames: z.array(z.string()).optional(), // Yapılan hizmetler
  createdAt: z.number(),
});

export const insertCorporateDebtSchema = corporateDebtSchema.omit({
  id: true,
  createdAt: true,
});
export type CorporateDebt = z.infer<typeof corporateDebtSchema>;
export type InsertCorporateDebt = z.infer<typeof insertCorporateDebtSchema>;

// Quote Line Item - Teklif kalemi (hizmet × araç tipi × fiyat)
export const quoteLineItemSchema = z.object({
  serviceId: z.string(), // Hizmet ID
  serviceName: z.string(), // Hizmet adı
  vehicleType: z.enum(["sedan", "suv", "large"]), // Araç tipi
  quantity: z.number().min(1), // Araç sayısı
  unitPrice: z.number().min(0), // Birim fiyat
  frequency: z.string(), // Frekans (günlük/haftalık/aylık)
  frequencyMultiplier: z.number().min(1).default(1), // Frekans çarpanı (aylık = 1, haftalık = 4, vb.)
  lineTotal: z.number().min(0), // Satır toplamı
});

export type QuoteLineItem = z.infer<typeof quoteLineItemSchema>;

// Corporate Quote - Genişletilmiş teklif sistemi
export const corporateQuoteSchema = z.object({
  id: z.string(),
  quoteNumber: z.string().optional(), // Teklif numarası (örn: TKL-2025-001)
  
  // Firma bilgileri (backward compatibility için nullable)
  companyId: z.string().optional(), // CorporateCompany referansı
  companyName: z.string().min(1, "Şirket adı zorunludur"),
  taxNumber: z.string().optional(),
  industry: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  
  // İletişim bilgileri
  contactName: z.string().min(1, "Yetkili adı zorunludur"),
  contactEmail: z.string().email("Geçerli bir email girin"),
  contactPhone: z.string().min(1, "Telefon zorunludur"),
  contactTitle: z.string().optional(),
  
  // Filo bilgileri
  fleetComposition: fleetCompositionSchema,
  totalVehicles: z.number().min(1, "En az 1 araç olmalıdır"),
  cleaningFrequency: z.enum([
    CleaningFrequency.DAILY,
    CleaningFrequency.WEEKLY,
    CleaningFrequency.BIWEEKLY,
    CleaningFrequency.MONTHLY,
    CleaningFrequency.CUSTOM,
  ]),
  customFrequency: z.string().optional(),
  
  // Teklif detayları
  lineItems: z.array(quoteLineItemSchema).optional(), // Teklif kalemleri
  subtotal: z.number().min(0).optional(), // Ara toplam
  discount: z.number().min(0).default(0), // İndirim (TL)
  discountPercentage: z.number().min(0).max(100).default(0), // İndirim (%)
  tax: z.number().min(0).default(0), // KDV (TL)
  taxPercentage: z.number().min(0).max(100).default(20), // KDV oranı (%)
  total: z.number().min(0).optional(), // Toplam tutar
  
  // Ödeme ve geçerlilik
  paymentTerms: z.string().optional(), // Ödeme koşulları
  validUntil: z.string().optional(), // Teklif geçerlilik tarihi (YYYY-MM-DD)
  deliveryTerms: z.string().optional(), // Teslimat/Hizmet koşulları
  
  // Sözleşme dönemi (ay cinsinden)
  contractDuration: z.number().min(1).max(24).default(1), // 1, 3, 6, 12 ay gibi
  monthlyWashCount: z.number().min(0).optional(), // Aylık toplam yıkama sayısı
  
  // Notlar
  notes: z.string().optional(), // Müşteri notu
  quotedNotes: z.string().optional(), // Admin'in teklif notu
  internalNotes: z.string().optional(), // İç notlar (müşteri görmez)
  
  // Durum ve tarihçe
  status: z.enum([
    QuoteStatus.PENDING,
    QuoteStatus.REVIEWED,
    QuoteStatus.QUOTED,
    QuoteStatus.SENT,
    QuoteStatus.ACCEPTED,
    QuoteStatus.REJECTED,
  ]).default(QuoteStatus.PENDING),
  statusHistory: z.array(z.object({
    status: z.string(),
    timestamp: z.number(),
    updatedBy: z.string().optional(),
  })).optional(),
  
  // PDF
  generatedPdfUrl: z.string().optional(), // PDF URL
  
  // Legacy alanlar (backward compatibility)
  quotedAmount: z.number().nullable().optional(), // Eski basit teklif tutarı
  
  // Metadata
  createdAt: z.number(),
  updatedAt: z.number().optional(),
  createdBy: z.string().optional(), // Admin ID
  updatedBy: z.string().optional(), // Admin ID
});

export const insertCorporateQuoteSchema = corporateQuoteSchema.omit({
  id: true,
  quoteNumber: true,
  status: true,
  statusHistory: true,
  generatedPdfUrl: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
});

export type InsertCorporateQuote = z.infer<typeof insertCorporateQuoteSchema>;
export type CorporateQuote = z.infer<typeof corporateQuoteSchema>;

// ============================================
// PAYMENT (Ödeme Kaydı) SCHEMA
// ============================================

export const paymentSchema = z.object({
  id: z.string(),
  cashierId: z.string(), // Ödemeyi kaydeden kasiyer
  plate: z.string().min(1, "Plaka zorunludur"), // Araç plakası
  serviceIds: z.array(z.string()).optional(), // Seçilen hizmet ID'leri
  serviceNames: z.string().min(1, "Hizmet bilgisi zorunludur"), // Hangi hizmetler (text olarak veya serviceIds'den generate)
  amount: z.number().min(0, "Tutar 0'dan büyük olmalıdır"), // Ödeme tutarı
  paymentMethod: z.enum([
    PaymentMethod.CASH,
    PaymentMethod.CARD,
    PaymentMethod.TRANSFER,
  ]),
  scheduledDate: z.string(), // YYYY-MM-DD format (ödeme tarihi)
  isCorporate: z.boolean().default(false), // Şirket müşterisi mi?
  companyId: z.string().nullable().optional(), // Kurumsal müşteri ise şirket ID
  willInvoice: z.boolean().default(false), // Fatura kesilecek mi?
  notes: z.string().optional(), // Ek notlar
  createdAt: z.number(), // Oluşturulma zamanı
});

// Subscription Payment Record Schema (separate collection)
export const subscriptionPaymentRecordSchema = z.object({
  id: z.string(),
  subscriptionId: z.string(),
  userId: z.string(),
  packageId: z.string(),
  amount: z.number().min(0),
  paymentMethod: z.enum([PaymentMethod.CASH, PaymentMethod.CARD, PaymentMethod.TRANSFER]),
  cashierId: z.string(),
  paidAt: z.number(),
  notes: z.string().optional(),
  createdAt: z.number(),
});

export const insertSubscriptionPaymentRecordSchema = subscriptionPaymentRecordSchema.omit({
  id: true,
  createdAt: true,
});

export type SubscriptionPaymentRecord = z.infer<typeof subscriptionPaymentRecordSchema>;
export type InsertSubscriptionPaymentRecord = z.infer<typeof insertSubscriptionPaymentRecordSchema>;

export const insertPaymentSchema = paymentSchema.omit({
  id: true,
  createdAt: true,
});
export type InsertPayment = z.infer<typeof insertPaymentSchema>;
export type Payment = z.infer<typeof paymentSchema>;

// ============================================
// CUSTOMER LOYALTY SCHEMA
// ============================================

export const customerLoyaltySchema = z.object({
  id: z.string(),
  customerId: z.string(), // User ID
  points: z.number().min(0).default(0), // Sadakat puanı (her yıkama +1 = her 5 yıkamada 1 hediye)
  totalSpent: z.number().min(0).default(0), // Toplam harcama (TL)
  totalWashes: z.number().min(0).default(0), // Toplam yıkama sayısı
  lastVisit: z.number().nullable(), // Son ziyaret zamanı (timestamp)
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCustomerLoyaltySchema = customerLoyaltySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCustomerLoyalty = z.infer<typeof insertCustomerLoyaltySchema>;
export type CustomerLoyalty = z.infer<typeof customerLoyaltySchema>;

// Basit sadakat programı konfigürasyonu
// Her 5 yıkamada 1 hediye - seviyeler yok
export const LOYALTY_CONFIG = {
  POINTS_PER_WASH: 1, // Her yıkama için 1 puan
  FREE_WASH_POINTS: 5, // 5 puan = 1 bedava yıkama kuponu
  FREE_WASH_VALUE: 50, // Bedava yıkama değeri (TL)
} as const;

// ============================================
// LOYALTY REDEMPTION SCHEMA
// ============================================

export const loyaltyRedemptionSchema = z.object({
  id: z.string(),
  customerId: z.string(), // Müşteri ID
  loyaltyId: z.string(), // CustomerLoyalty ID
  pointsRedeemed: z.number().min(1), // Kullanılan puan
  redemptionValue: z.number().min(0), // Kullanım değeri (TL)
  redeemedBy: z.string(), // İşlemi yapan kasiyer/admin ID
  notes: z.string().optional(), // Notlar
  createdAt: z.number(), // Kullanım zamanı
});

export const insertLoyaltyRedemptionSchema = loyaltyRedemptionSchema.omit({
  id: true,
  createdAt: true,
});
export type InsertLoyaltyRedemption = z.infer<typeof insertLoyaltyRedemptionSchema>;
export type LoyaltyRedemption = z.infer<typeof loyaltyRedemptionSchema>;

// ============================================
// REWARD COUPON SCHEMA (QR Kodlu Hediye Kuponları)
// ============================================

export const RewardCouponStatus = {
  ACTIVE: "active", // Kullanılabilir
  USED: "used", // Kullanıldı
  EXPIRED: "expired", // Süresi doldu
} as const;

export const RewardCouponType = {
  FREE_WASH: "free_wash", // Ücretsiz yıkama
  FREE_INTERIOR: "free_interior", // Ücretsiz iç temizlik
  FREE_MOTOR: "free_motor", // Ücretsiz motor yıkama
  FREE_COFFEE: "free_coffee", // Hediye kahve/içecek
  DISCOUNT: "discount", // Özel indirim
} as const;

export const rewardCouponSchema = z.object({
  id: z.string(),
  customerId: z.string(), // Kupon sahibi müşteri ID
  code: z.string(), // Benzersiz QR kod/kupon kodu
  type: z.enum(["free_wash", "free_interior", "free_motor", "free_coffee", "discount"]),
  title: z.string(), // Kupon başlığı (örn: "Ücretsiz Dış Yıkama")
  description: z.string().optional(), // Detaylı açıklama
  value: z.number().min(0).default(0), // Kupon değeri (TL veya %)
  status: z.enum(["active", "used", "expired"]).default("active"),
  pointsUsed: z.number().min(1).default(5), // Kaç puan karşılığında verildi
  expiresAt: z.number(), // Son kullanma tarihi (timestamp)
  usedAt: z.number().nullable().optional(), // Kullanıldığı tarih
  usedBy: z.string().nullable().optional(), // Kullanan kasiyer ID
  washId: z.string().nullable().optional(), // Kullanıldığı yıkama ID
  createdAt: z.number(),
});

export const insertRewardCouponSchema = rewardCouponSchema.omit({
  id: true,
  status: true,
  usedAt: true,
  usedBy: true,
  washId: true,
  createdAt: true,
});
export type InsertRewardCoupon = z.infer<typeof insertRewardCouponSchema>;
export type RewardCoupon = z.infer<typeof rewardCouponSchema>;

// ============================================
// GIFT (Hediye) SCHEMA
// ============================================

export const giftSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Hediye adı zorunludur"), // Hediye adı (örn: "Ücretsiz Dış Yıkama")
  description: z.string().min(1, "Açıklama zorunludur"), // Detaylı açıklama
  probability: z.number().min(0).max(100).default(10), // 0-100 arası gönderilme şansı (%)
  active: z.boolean().default(true), // Aktif/pasif
  createdAt: z.number(),
});

export const insertGiftSchema = giftSchema.omit({
  id: true,
  createdAt: true,
});
export type InsertGift = z.infer<typeof insertGiftSchema>;
export type Gift = z.infer<typeof giftSchema>;

// ============================================
// FEEDBACK (Complaints & Suggestions) SCHEMA
// ============================================

export enum FeedbackType {
  COMPLAINT = "complaint",
  SUGGESTION = "suggestion",
}

export enum FeedbackStatus {
  NEW = "new",
  REVIEWING = "reviewing",
  RESOLVED = "resolved",
}

export const feedbackSchema = z.object({
  id: z.string(),
  name: z.string().optional(), // Anonim olabilir
  email: z.string().email().optional().or(z.literal("")), // Boş string veya geçerli email
  phone: z.string().optional(),
  type: z.enum(["complaint", "suggestion"]), // String literals
  subject: z.string().min(1, "Konu zorunludur"),
  message: z.string().min(10, "Mesaj en az 10 karakter olmalıdır"),
  status: z.enum(["new", "reviewing", "resolved"]).default("new"),
  userId: z.string().optional(), // Kayıtlı kullanıcı varsa
  adminNotes: z.string().optional(), // Admin notları
  createdAt: z.number(),
  resolvedAt: z.number().optional(),
});

export const insertFeedbackSchema = feedbackSchema.omit({
  id: true,
  status: true,
  adminNotes: true,
  createdAt: true,
  resolvedAt: true,
});
export type InsertFeedback = z.infer<typeof insertFeedbackSchema>;
export type Feedback = z.infer<typeof feedbackSchema>;

// ============================================
// BUSINESS HOURS
// ============================================

// DEFAULT values - will be overridden by Firestore settings
export const BUSINESS_HOURS = {
  OPEN: "08:00",
  CLOSE: "19:00",
  EARLIEST_APPOINTMENT: "09:00", // Ertesi gün en erken
  MAX_CONCURRENT_CARS: 3, // Aynı anda max 3 araç
} as const;

// ============================================
// SYSTEM SETTINGS SCHEMA (Admin Configurable)
// ============================================

export const systemSettingsSchema = z.object({
  id: z.string(),
  
  // Loyalty Ayarları
  loyalty: z.object({
    pointsPerWash: z.number().min(1).max(10).default(1),
    freeWashPoints: z.number().min(1).max(50).default(5),
    freeWashValue: z.number().min(0).max(500).default(50),
    tierThresholds: z.object({
      bronze: z.number().min(0).default(0),
      silver: z.number().min(0).default(1000),
      gold: z.number().min(0).default(5000),
    }),
    tierDiscounts: z.object({
      bronze: z.number().min(0).max(100).default(0),
      silver: z.number().min(0).max(100).default(5),
      gold: z.number().min(0).max(100).default(10),
    }),
  }),
  
  // İş Saatleri & Kapasite
  operations: z.object({
    businessHours: z.object({
      open: z.string().regex(/^\d{2}:\d{2}$/).default("08:00"),
      close: z.string().regex(/^\d{2}:\d{2}$/).default("19:00"),
      earliestAppointment: z.string().regex(/^\d{2}:\d{2}$/).default("09:00"),
    }),
    capacity: z.object({
      maxConcurrentCars: z.number().min(1).max(10).default(3),
      slotDurationMinutes: z.number().min(15).max(120).default(60),
    }),
  }),
  
  // KVKK & Arşivleme
  compliance: z.object({
    photoRetentionDays: z.number().min(1).max(365).default(7),
    dataRetentionDays: z.number().min(30).max(3650).default(90),
  }),
  
  updatedAt: z.number(),
  updatedBy: z.string(), // Admin ID
});

export const insertSystemSettingsSchema = systemSettingsSchema.omit({
  id: true,
  updatedAt: true,
});

export type SystemSettings = z.infer<typeof systemSettingsSchema>;
export type InsertSystemSettings = z.infer<typeof insertSystemSettingsSchema>;

// ============================================
// BRAND SETTINGS SCHEMA (Web Builder)
// ============================================

export const brandSettingsSchema = z.object({
  id: z.string(),
  
  // Setup Wizard Status
  setupCompleted: z.boolean().default(false), // İlk kurulum tamamlandı mı?
  demoDataLoaded: z.boolean().default(false), // Demo data yüklendi mi?
  activeThemePreset: z.string().optional(), // Aktif tema preset ID (modern-blue, professional-gray, vb.)
  
  // Logo & Branding
  logoUrl: z.string().optional(),
  logoText: z.string().default("TemizFilo"),
  favicon: z.string().optional(),
  
  // Theme Colors (HSL format: "210 100% 56%")
  colors: z.object({
    primary: z.string().default("210 100% 56%"),
    secondary: z.string().default("199 89% 80%"),
    accent: z.string().default("199 89% 94%"),
    destructive: z.string().default("356 90% 54%"),
    background: z.string().default("0 0% 100%"),
    foreground: z.string().default("0 0% 10%"),
    muted: z.string().default("0 0% 95%"),
  }),
  
  // Hero Section
  hero: z.object({
    title: z.string().default("Aracınız İçin Mükemmel Bakım"),
    subtitle: z.string().default("Profesyonel ekibimiz ve modern ekipmanlarımızla aracınıza hak ettiği bakımı sunuyoruz"),
    badge: z.string().default("Premium Araç Yıkama Hizmeti"),
    cta1Text: z.string().default("Hemen Randevu Al"),
    cta1Link: z.string().default("/book"),
    cta2Text: z.string().default("Randevu Takip"),
    cta2Link: z.string().default("/track"),
    backgroundUrl: z.string().optional(),
    backgroundType: z.enum(["gradient", "image", "solid"]).default("gradient"),
  }),
  
  // Stats Section
  stats: z.array(z.object({
    value: z.string(),
    label: z.string(),
    icon: z.string().default("users"),
  })).default([
    { value: "10,000+", label: "Mutlu Müşteri", icon: "users" },
    { value: "4.9/5", label: "Ortalama Puan", icon: "star" },
    { value: "15+", label: "Yıllık Tecrübe", icon: "award" },
    { value: "24/7", label: "Müşteri Desteği", icon: "clock" },
  ]),
  
  // Social Media
  socialMedia: z.object({
    facebook: z.string().optional(),
    instagram: z.string().optional(),
    twitter: z.string().optional(),
    linkedin: z.string().optional(),
    youtube: z.string().optional(),
  }).optional(),
  
  // Footer
  footer: z.object({
    description: z.string().default("Profesyonel araç yıkama ve bakım hizmetleri"),
    copyrightText: z.string().default("Tüm hakları saklıdır"),
  }),
  
  // SEO
  seo: z.object({
    metaTitle: z.string().default("TemizFilo - Premium Araç Yıkama Hizmeti"),
    metaDescription: z.string().default("Profesyonel araç yıkama ve bakım hizmetleri. Online randevu alın, QR kod ile takip edin."),
    metaKeywords: z.string().default("araç yıkama, oto yıkama, detaylı temizlik"),
  }).optional(),
  
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertBrandSettingsSchema = brandSettingsSchema.omit({ id: true, updatedAt: true });
export type BrandSettings = z.infer<typeof brandSettingsSchema>;
export type InsertBrandSettings = z.infer<typeof insertBrandSettingsSchema>;

// ============================================
// FAQ SCHEMA
// ============================================

export const faqSchema = z.object({
  id: z.string(),
  question: z.string().min(1, "Soru gerekli"),
  answer: z.string().min(1, "Cevap gerekli"),
  category: z.enum(["genel", "randevu", "fiyatlandirma", "hizmetler", "kvkk", "kurumsal"]).default("genel"),
  order: z.number().default(0),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertFaqSchema = faqSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type Faq = z.infer<typeof faqSchema>;
export type InsertFaq = z.infer<typeof insertFaqSchema>;

// ============================================
// FEATURE SCHEMA (Özellikler)
// ============================================

export const featureSchema = z.object({
  id: z.string(),
  title: z.string().min(1, "Başlık gerekli"),
  description: z.string().min(1, "Açıklama gerekli"),
  icon: z.string().default("check"), // Lucide icon name
  order: z.number().default(0),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertFeatureSchema = featureSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type Feature = z.infer<typeof featureSchema>;
export type InsertFeature = z.infer<typeof insertFeatureSchema>;

// ============================================
// HOME PAGE SECTION SCHEMA
// ============================================

export const homePageSectionSchema = z.object({
  id: z.string(),
  type: z.enum(["hero", "stats", "features", "services", "faq", "contact", "cta"]),
  enabled: z.boolean().default(true),
  order: z.number().default(0),
  settings: z.record(z.any()).optional(), // Flexible settings per section
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertHomePageSectionSchema = homePageSectionSchema.omit({ id: true, updatedAt: true });
export type HomePageSection = z.infer<typeof homePageSectionSchema>;
export type InsertHomePageSection = z.infer<typeof insertHomePageSectionSchema>;

// ============================================
// HELPER TYPES
// ============================================

export type PopulatedAppointment = Appointment & {
  customer?: User;
  vehicle?: Vehicle;
  service?: Service;
};

export type PopulatedWash = Wash & {
  customer?: User;
  vehicle?: Vehicle;
  service?: Service;
  employee?: EmployeeProfile;
  appointment?: Appointment;
  review?: Review;
};

export type PopulatedReview = Review & {
  customer?: User;
  wash?: PopulatedWash;
};

// ============================================
// SMS INTEGRATION SCHEMAS
// ============================================

export const SmsType = {
  WELCOME: "welcome", // Hoş geldiniz SMS
  APPOINTMENT_CONFIRMED: "appointment_confirmed", // Randevu onayı
  APPOINTMENT_REMINDER: "appointment_reminder", // Randevu hatırlatma
  WASH_READY: "wash_ready", // Araç hazır
  CAMPAIGN: "campaign", // Kampanya SMS
  LOYALTY_POINTS: "loyalty_points", // Sadakat puanı
  LOYALTY_GIFT: "loyalty_gift", // Sadakat hediyesi gönderimi
  OTP: "otp", // Tek kullanımlık şifre
  CUSTOM: "custom", // Özel SMS
} as const;

export const SmsStatus = {
  PENDING: "pending", // Gönderilecek
  SENT: "sent", // Gönderildi
  DELIVERED: "delivered", // Teslim edildi
  FAILED: "failed", // Başarısız
} as const;

// SMS Log - Gönderilen tüm SMS'lerin kaydı
export const smsLogSchema = z.object({
  id: z.string(),
  userId: z.string().optional(), // Hangi kullanıcıya gönderildi
  phoneNumber: z.string(), // Alıcı telefon numarası (5xxxxxxxxx formatı)
  message: z.string(), // Gönderilen mesaj metni
  type: z.enum([
    SmsType.WELCOME,
    SmsType.APPOINTMENT_CONFIRMED,
    SmsType.APPOINTMENT_REMINDER,
    SmsType.WASH_READY,
    SmsType.CAMPAIGN,
    SmsType.LOYALTY_POINTS,
    SmsType.LOYALTY_GIFT,
    SmsType.OTP,
    SmsType.CUSTOM,
  ]),
  status: z.enum([
    SmsStatus.PENDING,
    SmsStatus.SENT,
    SmsStatus.DELIVERED,
    SmsStatus.FAILED,
  ]).default("pending"),
  orderId: z.string().optional(), // İletiMerkezi sipariş ID'si
  errorMessage: z.string().optional(), // Hata mesajı
  relatedEntityId: z.string().optional(), // İlgili randevu/yıkama/kullanıcı ID'si
  sentAt: z.number().optional(), // Gönderilme zamanı
  deliveredAt: z.number().optional(), // Teslim edilme zamanı
  createdAt: z.number(),
});

export const insertSmsLogSchema = smsLogSchema.omit({
  id: true,
  createdAt: true,
});

export type SmsLog = z.infer<typeof smsLogSchema>;
export type InsertSmsLog = z.infer<typeof insertSmsLogSchema>;

// SMS Template - SMS şablonları
export const smsTemplateSchema = z.object({
  id: z.string(),
  type: z.enum([
    SmsType.WELCOME,
    SmsType.APPOINTMENT_CONFIRMED,
    SmsType.APPOINTMENT_REMINDER,
    SmsType.WASH_READY,
    SmsType.CAMPAIGN,
    SmsType.LOYALTY_POINTS,
    SmsType.LOYALTY_GIFT,
    SmsType.OTP,
    SmsType.CUSTOM,
  ]),
  name: z.string(), // Şablon adı (örn: "Hoş Geldiniz SMS")
  text: z.string(), // SMS metni (değişkenler kullanılabilir: {{name}}, {{date}}, vb.)
  variables: z.array(z.string()).default([]), // Kullanılan değişkenler listesi
  enabled: z.boolean().default(true), // Aktif/pasif
  iysRequired: z.boolean().default(true), // IYS sorgulaması gerekli mi?
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertSmsTemplateSchema = smsTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SmsTemplate = z.infer<typeof smsTemplateSchema>;
export type InsertSmsTemplate = z.infer<typeof insertSmsTemplateSchema>;

// SMS Settings - SMS sistem ayarları
export const smsSettingsSchema = z.object({
  id: z.string(),
  apiKey: z.string().optional(), // İletiMerkezi API Anahtarı
  apiHash: z.string().optional(), // İletiMerkezi API Hash
  sender: z.string().default("TEMIZFILO"), // SMS başlığı (Gönderici Adı)
  balance: z.number().default(0), // Kalan SMS bakiyesi
  iysEnabled: z.boolean().default(true), // IYS sorgulaması aktif mi?
  iysList: z.enum(["BIREYSEL", "TACIR"]).default("BIREYSEL"), // IYS liste türü
  lastBalanceCheck: z.number().optional(), // Son bakiye kontrolü
  updatedAt: z.number(),
});

export const insertSmsSettingsSchema = smsSettingsSchema.omit({
  id: true,
  updatedAt: true,
});

export type SmsSettings = z.infer<typeof smsSettingsSchema>;
export type InsertSmsSettings = z.infer<typeof insertSmsSettingsSchema>;

// Campaign SMS Template - Kampanya SMS şablonları
export const campaignSmsTemplateSchema = z.object({
  id: z.string(),
  name: z.string(), // Şablon adı (örn: "Kış Kampanyası")
  message: z.string(), // SMS mesajı (placeholder'lar kullanılabilir: {{customerName}}, {{companyName}}, vb.)
  category: z.enum([
    "seasonal", // Mevsimsel (Kış, Yaz kampanyası vb.)
    "discount", // İndirim
    "loyalty", // Sadakat programı
    "service", // Yeni hizmet duyurusu
    "event", // Özel gün/etkinlik
    "reminder", // Hatırlatma
    "feedback", // Geri bildirim talebi
    "general", // Genel duyuru
  ]),
  description: z.string().optional(), // Şablonun açıklaması
  placeholders: z.array(z.string()).default([]), // Kullanılan placeholder'lar (customerName, companyName, etc.)
  targetAudience: z.array(z.enum([
    "all", // Tüm kullanıcılar
    "registered", // Kayıtlı kullanıcılar
    "unregistered", // Kayıtsız (guest) kullanıcılar
    "corporate", // Kurumsal firmalar
    "customer", // Bireysel müşteriler
    "employee", // Çalışanlar
  ])).default(["all"]), // Önerilen hedef kitle
  enabled: z.boolean().default(true), // Aktif/pasif
  usageCount: z.number().default(0), // Kaç kez kullanıldı
  lastUsedAt: z.number().optional(), // Son kullanım zamanı
  createdBy: z.string().optional(), // Oluşturan admin ID
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCampaignSmsTemplateSchema = campaignSmsTemplateSchema.omit({
  id: true,
  usageCount: true,
  lastUsedAt: true,
  createdAt: true,
  updatedAt: true,
});

export type CampaignSmsTemplate = z.infer<typeof campaignSmsTemplateSchema>;
export type InsertCampaignSmsTemplate = z.infer<typeof insertCampaignSmsTemplateSchema>;

// SMS Statistics - G SMS İstatistikleri (Günlük/Aylık)
export const smsStatisticsSchema = z.object({
  id: z.string(),
  date: z.string(), // YYYY-MM-DD formatında tarih (günlük) veya YYYY-MM (aylık)
  period: z.enum(["daily", "monthly"]), // İstatistik periyodu
  totalSent: z.number().default(0), // Toplam gönderilen SMS
  totalSuccess: z.number().default(0), // Başarılı gönderimler
  totalFailed: z.number().default(0), // Başarısız gönderimler
  successRate: z.number().default(0), // Başarı oranı (0-100)
  byType: z.record(z.object({
    sent: z.number(),
    success: z.number(),
    failed: z.number(),
  })).optional(), // SMS tipine göre detay (welcome, campaign, vb.)
  byCampaign: z.record(z.object({
    sent: z.number(),
    success: z.number(),
    failed: z.number(),
    templateName: z.string().optional(),
  })).optional(), // Kampanya şablonuna göre detay
  estimatedCost: z.number().default(0), // Tahmini maliyet (kredi tüketimi)
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertSmsStatisticsSchema = smsStatisticsSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SmsStatistics = z.infer<typeof smsStatisticsSchema>;
export type InsertSmsStatistics = z.infer<typeof insertSmsStatisticsSchema>;

// ============================================
// INVENTORY MANAGEMENT SCHEMA
// ============================================

export const InventoryCategory = {
  CLEANING: "cleaning", // Temizlik Malzemeleri (şampuan, deterjan)
  POLISH: "polish", // Cilalama Ürünleri
  TOOLS: "tools", // Ekipman (havlu, fırça)
  CHEMICAL: "chemical", // Kimyasal Ürünler
  OTHER: "other", // Diğer
} as const;

export const InventoryUnit = {
  PIECE: "piece", // Adet
  LITER: "liter", // Litre
  KG: "kg", // Kilogram
  BOX: "box", // Kutu
  BOTTLE: "bottle", // Şişe
} as const;

// Inventory Item - Stok kalemleri
export const inventoryItemSchema = z.object({
  id: z.string(),
  name: z.string(), // Malzeme adı (örn: "Oto Şampuanı")
  category: z.enum([
    InventoryCategory.CLEANING,
    InventoryCategory.POLISH,
    InventoryCategory.TOOLS,
    InventoryCategory.CHEMICAL,
    InventoryCategory.OTHER,
  ]),
  unit: z.enum([
    InventoryUnit.PIECE,
    InventoryUnit.LITER,
    InventoryUnit.KG,
    InventoryUnit.BOX,
    InventoryUnit.BOTTLE,
  ]),
  currentStock: z.number().default(0), // Mevcut stok miktarı
  criticalLevel: z.number().default(10), // Kritik stok seviyesi (altında uyarı)
  unitPrice: z.number().default(0), // Birim fiyat (TL)
  supplier: z.string().optional(), // Tedarikçi
  barcode: z.string().optional(), // Barkod numarası
  notes: z.string().optional(), // Notlar
  lastRestockDate: z.number().optional(), // Son stok yenileme tarihi
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertInventoryItemSchema = inventoryItemSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InventoryItem = z.infer<typeof inventoryItemSchema>;
export type InsertInventoryItem = z.infer<typeof insertInventoryItemSchema>;

// Stock Movement - Stok hareketleri (giriş/çıkış takibi)
export const StockMovementType = {
  IN: "in", // Giriş (satın alma, iade)
  OUT: "out", // Çıkış (kullanım, satış)
  ADJUSTMENT: "adjustment", // Düzeltme (sayım farkı)
} as const;

export const stockMovementSchema = z.object({
  id: z.string(),
  itemId: z.string(), // Malzeme ID
  type: z.enum([
    StockMovementType.IN,
    StockMovementType.OUT,
    StockMovementType.ADJUSTMENT,
  ]),
  quantity: z.number(), // Miktar (+ veya -)
  reason: z.string().optional(), // Sebep (örn: "Satın alma", "Yıkama kullanımı")
  performedBy: z.string(), // İşlemi yapan admin/employee ID
  relatedEntityId: z.string().optional(), // İlgili belge ID (expense, wash vb.)
  notes: z.string().optional(),
  createdAt: z.number(),
});

export const insertStockMovementSchema = stockMovementSchema.omit({
  id: true,
  createdAt: true,
});

export type StockMovement = z.infer<typeof stockMovementSchema>;
export type InsertStockMovement = z.infer<typeof insertStockMovementSchema>;

// ============================================
// EMAIL TEMPLATE SYSTEM
// ============================================

export const EmailType = {
  WELCOME: "welcome", // Hoş geldiniz email
  APPOINTMENT_CONFIRMED: "appointment_confirmed", // Randevu onayı
  APPOINTMENT_REMINDER: "appointment_reminder", // Randevu hatırlatma
  WASH_COMPLETED: "wash_completed", // Yıkama tamamlandı
  PAYMENT_RECEIPT: "payment_receipt", // Ödeme makbuzu
  LOYALTY_UPDATE: "loyalty_update", // Sadakat programı güncelleme
  CAMPAIGN: "campaign", // Kampanya maili
  PASSWORD_RESET: "password_reset", // Şifre sıfırlama
  CUSTOM: "custom", // Özel email
} as const;

export const EmailStatus = {
  PENDING: "pending", // Gönderilecek
  SENT: "sent", // Gönderildi
  DELIVERED: "delivered", // Teslim edildi
  FAILED: "failed", // Başarısız
  OPENED: "opened", // Açıldı
  CLICKED: "clicked", // Link tıklandı
} as const;

// Email Template - Email şablonları
export const emailTemplateSchema = z.object({
  id: z.string(),
  type: z.enum([
    EmailType.WELCOME,
    EmailType.APPOINTMENT_CONFIRMED,
    EmailType.APPOINTMENT_REMINDER,
    EmailType.WASH_COMPLETED,
    EmailType.PAYMENT_RECEIPT,
    EmailType.LOYALTY_UPDATE,
    EmailType.CAMPAIGN,
    EmailType.PASSWORD_RESET,
    EmailType.CUSTOM,
  ]),
  name: z.string(), // Şablon adı (örn: "Hoş Geldiniz Email")
  subject: z.string(), // Email konusu (değişkenler kullanılabilir: {{name}}, {{date}}, vb.)
  htmlContent: z.string(), // HTML içerik
  textContent: z.string().optional(), // Plain text alternatif
  variables: z.array(z.string()).default([]), // Kullanılan değişkenler listesi
  fromName: z.string().default("TemizFilo"), // Gönderen adı
  replyToEmail: z.string().email().optional(), // Cevap adresi
  enabled: z.boolean().default(true), // Aktif/pasif
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(), // Admin ID
});

export const insertEmailTemplateSchema = emailTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type EmailTemplate = z.infer<typeof emailTemplateSchema>;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;

// Email Log - Gönderilen email kayıtları
export const emailLogSchema = z.object({
  id: z.string(),
  userId: z.string().optional(), // Hangi kullanıcıya gönderildi
  recipientEmail: z.string().email(), // Alıcı email adresi
  recipientName: z.string().optional(), // Alıcı adı
  subject: z.string(), // Email konusu
  htmlContent: z.string(), // Gönderilen HTML içerik
  type: z.enum([
    EmailType.WELCOME,
    EmailType.APPOINTMENT_CONFIRMED,
    EmailType.APPOINTMENT_REMINDER,
    EmailType.WASH_COMPLETED,
    EmailType.PAYMENT_RECEIPT,
    EmailType.LOYALTY_UPDATE,
    EmailType.CAMPAIGN,
    EmailType.PASSWORD_RESET,
    EmailType.CUSTOM,
  ]),
  status: z.enum([
    EmailStatus.PENDING,
    EmailStatus.SENT,
    EmailStatus.DELIVERED,
    EmailStatus.FAILED,
    EmailStatus.OPENED,
    EmailStatus.CLICKED,
  ]).default("pending"),
  emailServiceId: z.string().optional(), // SendGrid/Mailgun message ID
  errorMessage: z.string().optional(), // Hata mesajı
  relatedEntityId: z.string().optional(), // İlgili randevu/yıkama/kullanıcı ID'si
  sentAt: z.number().optional(), // Gönderilme zamanı
  deliveredAt: z.number().optional(), // Teslim edilme zamanı
  openedAt: z.number().optional(), // Açılma zamanı
  clickedAt: z.number().optional(), // Tıklama zamanı
  createdAt: z.number(),
});

export const insertEmailLogSchema = emailLogSchema.omit({
  id: true,
  createdAt: true,
});

export type EmailLog = z.infer<typeof emailLogSchema>;
export type InsertEmailLog = z.infer<typeof insertEmailLogSchema>;

// Email Settings - Email sistem ayarları
export const emailSettingsSchema = z.object({
  id: z.string(),
  provider: z.enum(["smtp", "sendgrid", "mailgun", "resend"]).default("smtp"), // Email provider
  
  // SMTP Settings
  smtpHost: z.string().optional(),
  smtpPort: z.number().optional(),
  smtpUser: z.string().optional(),
  smtpPassword: z.string().optional(),
  smtpSecure: z.boolean().default(true), // TLS/SSL
  
  // SendGrid/Mailgun API
  apiKey: z.string().optional(),
  
  // Sender Info
  fromEmail: z.string().email().default("noreply@temizfilo.com"),
  fromName: z.string().default("TemizFilo"),
  replyToEmail: z.string().email().optional(),
  
  // Limits
  dailyLimit: z.number().default(1000), // Günlük email limiti
  sentToday: z.number().default(0), // Bugün gönderilen
  lastResetDate: z.string().optional(), // Son sıfırlama tarihi
  
  enabled: z.boolean().default(true), // Email sistemi aktif mi?
  updatedAt: z.number(),
  updatedBy: z.string(), // Admin ID
});

export const insertEmailSettingsSchema = emailSettingsSchema.omit({
  id: true,
  updatedAt: true,
});

export type EmailSettings = z.infer<typeof emailSettingsSchema>;
export type InsertEmailSettings = z.infer<typeof insertEmailSettingsSchema>;

// ============================================
// TESTIMONIALS SCHEMA (Müşteri Yorumları)
// ============================================

export const testimonialSchema = z.object({
  id: z.string(),
  customerName: z.string().min(1, "Müşteri adı gerekli"),
  customerPhoto: z.string().optional(), // URL
  rating: z.number().min(1).max(5).default(5),
  comment: z.string().min(1, "Yorum gerekli"),
  position: z.string().optional(), // "Müşteri", "Kurumsal Müşteri" vb.
  company: z.string().optional(),
  featured: z.boolean().default(false), // Öne çıkan yorumlar
  order: z.number().default(0),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertTestimonialSchema = testimonialSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type Testimonial = z.infer<typeof testimonialSchema>;
export type InsertTestimonial = z.infer<typeof insertTestimonialSchema>;

// ============================================
// CONTACT SETTINGS SCHEMA
// ============================================

export const contactSettingsSchema = z.object({
  id: z.string(),
  
  // Konum & Harita
  address: z.string().default(""),
  mapEmbedUrl: z.string().optional(), // Google Maps iframe URL
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  
  // İletişim
  phoneNumbers: z.array(z.string()).default([]),
  emails: z.array(z.string()).default([]),
  whatsappNumber: z.string().optional(), // Format: 905xxxxxxxxx
  whatsappMessage: z.string().default("Merhaba, bilgi almak istiyorum."),
  
  // Çalışma Saatleri
  workingHours: z.object({
    weekdays: z.string().default("09:00 - 18:00"),
    saturday: z.string().default("10:00 - 17:00"),
    sunday: z.string().default("Kapalı"),
  }),
  
  // Form Ayarları
  contactFormEnabled: z.boolean().default(true),
  autoReplyEnabled: z.boolean().default(true),
  autoReplyMessage: z.string().default("Mesajınız alındı. En kısa sürede dönüş yapacağız."),
  
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertContactSettingsSchema = contactSettingsSchema.omit({ id: true, updatedAt: true });
export type ContactSettings = z.infer<typeof contactSettingsSchema>;
export type InsertContactSettings = z.infer<typeof insertContactSettingsSchema>;

// ============================================
// GALLERY SCHEMA (Galeri - Before/After)
// ============================================

export const GalleryCategory = {
  BEFORE_AFTER: "before_after", // Before/After Görselleri
  INTERIOR: "interior", // İç Temizlik Görselleri
  EXTERIOR: "exterior", // Dış Yıkama Görselleri
  HERO_IMAGES: "hero_images", // Hero/Ana Sayfa Görselleri
  SERVICE_IMAGES: "service_images", // Hizmet Görselleri
  TEAM_IMAGES: "team_images", // Ekip Fotoğrafları
  FACILITY_IMAGES: "facility_images", // Tesis Görselleri
  TESTIMONIAL_IMAGES: "testimonial_images", // Müşteri Yorumları İçin Görseller
  GENERAL: "general", // Genel Kullanım
} as const;

export const galleryItemSchema = z.object({
  id: z.string(),
  title: z.string().min(1, "Başlık gerekli"),
  description: z.string().optional(),
  category: z.enum([
    GalleryCategory.BEFORE_AFTER,
    GalleryCategory.INTERIOR,
    GalleryCategory.EXTERIOR,
    GalleryCategory.HERO_IMAGES,
    GalleryCategory.SERVICE_IMAGES,
    GalleryCategory.TEAM_IMAGES,
    GalleryCategory.FACILITY_IMAGES,
    GalleryCategory.TESTIMONIAL_IMAGES,
    GalleryCategory.GENERAL,
  ]),
  // For before/after category
  beforePhotoUrl: z.string().optional(),
  afterPhotoUrl: z.string().optional(),
  // For other categories (single image)
  imageUrl: z.string().optional(),
  
  vehicleType: z.string().optional(), // "BMW 3.20i" vb. (for before/after)
  serviceUsed: z.string().optional(), // Hangi hizmet kullanıldı (for before/after)
  featured: z.boolean().default(false), // Anasayfada göster
  order: z.number().default(0),
  active: z.boolean().default(true),
  views: z.number().default(0), // Görüntülenme sayısı
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertGalleryItemSchema = galleryItemSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type GalleryItem = z.infer<typeof galleryItemSchema>;
export type InsertGalleryItem = z.infer<typeof insertGalleryItemSchema>;

// ============================================
// GALLERY PAGE SETTINGS SCHEMA
// ============================================

export const galleryPageSettingsSchema = z.object({
  id: z.string(),
  categoryTabs: z.object({
    beforeAfter: z.object({
      enabled: z.boolean().default(true),
      label: z.string().default("Önce/Sonra"),
      description: z.string().optional(),
    }),
    interior: z.object({
      enabled: z.boolean().default(true),
      label: z.string().default("İç Temizlik"),
      description: z.string().optional(),
    }),
    exterior: z.object({
      enabled: z.boolean().default(true),
      label: z.string().default("Dış Yıkama"),
      description: z.string().optional(),
    }),
  }),
  heroTitle: z.string().default("Öncesi ve Sonrası"),
  heroSubtitle: z.string().default("Müşterilerimizin araçlarına verdiğimiz özenin fotoğrafları"),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertGalleryPageSettingsSchema = galleryPageSettingsSchema.omit({ id: true, updatedAt: true });
export type GalleryPageSettings = z.infer<typeof galleryPageSettingsSchema>;
export type InsertGalleryPageSettings = z.infer<typeof insertGalleryPageSettingsSchema>;

// ============================================
// GALLERY CATEGORY SCHEMA (Dynamic Categories)
// ============================================

export const galleryCategorySchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Kategori adı gerekli"), // Teknik isim (slug): "before_after", "interior", etc.
  displayName: z.string().min(1, "Görünen ad gerekli"), // Kullanıcıya gösterilecek isim: "Önce/Sonra", "İç Temizlik"
  description: z.string().optional(), // Kategori açıklaması
  order: z.number().default(0), // Sıralama düzeni
  active: z.boolean().default(true), // Aktif/Pasif durumu
  icon: z.string().optional(), // Lucide icon adı (örn: "image", "sparkles")
  showInGallery: z.boolean().default(true), // Galeri sayfasında tab olarak göster
  allowBeforeAfter: z.boolean().default(false), // Bu kategori önce/sonra fotoğraflarını destekler mi?
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(), // Admin ID
});

export const insertGalleryCategorySchema = galleryCategorySchema.omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});

export type GalleryCategory_Type = z.infer<typeof galleryCategorySchema>;
export type InsertGalleryCategory = z.infer<typeof insertGalleryCategorySchema>;

// ============================================
// DEPLOYMENT SETTINGS SCHEMA (GitHub Actions)
// ============================================

export const deploymentSettingsSchema = z.object({
  id: z.string(),
  githubRepo: z.string().min(1, "GitHub repository gerekli"), // Format: "username/repo"
  githubToken: z.string().min(1, "GitHub token gerekli"),
  githubBranch: z.string().default("main"),
  autoDeployEnabled: z.boolean().default(false),
  lastDeployment: z.number().optional(), // Timestamp
  lastDeploymentStatus: z.enum(["success", "failure", "pending"]).optional(),
  lastDeploymentMessage: z.string().optional(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertDeploymentSettingsSchema = deploymentSettingsSchema.omit({ 
  id: true, 
  updatedAt: true 
});

export type DeploymentSettings = z.infer<typeof deploymentSettingsSchema>;
export type InsertDeploymentSettings = z.infer<typeof insertDeploymentSettingsSchema>;

// ============================================
// BLOG POST SCHEMA
// ============================================

export const BlogCategory = {
  TIPS: "tips", // İpuçları
  NEWS: "news", // Haberler
  CAMPAIGNS: "campaigns", // Kampanyalar
  GUIDES: "guides", // Rehberler
  MAINTENANCE: "maintenance", // Bakım
  TECHNOLOGY: "technology", // Teknoloji
} as const;

export const BlogStatus = {
  DRAFT: "draft", // Taslak
  PUBLISHED: "published", // Yayında
  SCHEDULED: "scheduled", // Zamanlanmış
  ARCHIVED: "archived", // Arşivlenmiş
} as const;

export const blogPostSchema = z.object({
  id: z.string(),
  title: z.string().min(1, "Başlık gerekli"),
  slug: z.string().min(1, "URL slug gerekli"), // SEO-friendly URL
  excerpt: z.string().optional(), // Kısa özet
  content: z.string().min(1, "İçerik gerekli"), // HTML veya Markdown
  featuredImageUrl: z.string().optional(),
  category: z.enum([
    BlogCategory.TIPS,
    BlogCategory.NEWS,
    BlogCategory.CAMPAIGNS,
    BlogCategory.GUIDES,
    BlogCategory.MAINTENANCE,
    BlogCategory.TECHNOLOGY,
  ]),
  tags: z.array(z.string()).default([]),
  status: z.enum([
    BlogStatus.DRAFT,
    BlogStatus.PUBLISHED,
    BlogStatus.SCHEDULED,
    BlogStatus.ARCHIVED,
  ]).default("draft"),
  
  // SEO
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  seoKeywords: z.string().optional(),
  
  // Stats
  views: z.number().default(0),
  likes: z.number().default(0),
  
  // Scheduling
  publishedAt: z.number().optional(),
  scheduledFor: z.number().optional(),
  
  author: z.string(), // Admin ID
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string(),
});

export const insertBlogPostSchema = blogPostSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type BlogPost = z.infer<typeof blogPostSchema>;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;

// ============================================
// POPUP (Pop-up Modals)
// ============================================

export const PopupPageId = {
  LANDING: "landing", // Ana Sayfa
  CUSTOMER_DASHBOARD: "customer-dashboard", // Müşteri Paneli
  BOOK: "book", // Rezervasyon Sayfası
  CORPORATE: "corporate", // Kurumsal Sayfa
} as const;

export const PopupDisplayFrequency = {
  ONCE: "once", // Bir kez göster (localStorage ile)
  ALWAYS: "always", // Her zaman göster
  DAILY: "daily", // Günde bir kez göster
} as const;

export const PopupStyle = {
  DEFAULT: "default", // Varsayılan - Mavi çerçeve
  SUCCESS: "success", // Başarı - Yeşil, kutlama
  WARNING: "warning", // Uyarı - Sarı/Turuncu
  INFO: "info", // Bilgi - Mavi, haber
  ANNOUNCEMENT: "announcement", // Duyuru - Mor, megafon
  PROMOTION: "promotion", // Kampanya - Kırmızı/Pembe, indirim
  CELEBRATION: "celebration", // Kutlama - Renkli, konfeti
} as const;

export const popupSchema = z.object({
  id: z.string(),
  pageId: z.enum([
    PopupPageId.LANDING,
    PopupPageId.CUSTOMER_DASHBOARD,
    PopupPageId.BOOK,
    PopupPageId.CORPORATE,
  ]),
  title: z.string().min(1, "Başlık gerekli"),
  content: z.string().min(1, "İçerik gerekli"), // HTML destekli
  enabled: z.boolean().default(true),
  displayFrequency: z.enum([
    PopupDisplayFrequency.ONCE,
    PopupDisplayFrequency.ALWAYS,
    PopupDisplayFrequency.DAILY,
  ]).default("once"),
  style: z.enum([
    PopupStyle.DEFAULT,
    PopupStyle.SUCCESS,
    PopupStyle.WARNING,
    PopupStyle.INFO,
    PopupStyle.ANNOUNCEMENT,
    PopupStyle.PROMOTION,
    PopupStyle.CELEBRATION,
  ]).default("default"),
  icon: z.string().optional(), // Emoji veya icon ismi (ör: "🎉", "🎁", "⚠️")
  buttonText: z.string().default("Tamam"),
  imageUrl: z.string().optional(), // Opsiyonel görsel
  priority: z.number().default(0), // Birden fazla pop-up varsa sıralama
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertPopupSchema = popupSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type Popup = z.infer<typeof popupSchema>;
export type InsertPopup = z.infer<typeof insertPopupSchema>;

// ============================================
// CORPORATE PRICING SCHEMA
// ============================================

// Şirketlere özel fiyatlandırma
export const corporatePricingSchema = z.object({
  id: z.string(),
  companyId: z.string(), // Hangi şirket için
  serviceId: z.string(), // Hangi hizmet için
  customPrice: z.number().min(0), // Özel fiyat (TL)
  isActive: z.boolean().default(true), // Aktif mi?
  notes: z.string().optional(), // Notlar
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCorporatePricingSchema = corporatePricingSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CorporatePricing = z.infer<typeof corporatePricingSchema>;
export type InsertCorporatePricing = z.infer<typeof insertCorporatePricingSchema>;

// ============================================
// CORPORATE INVOICE SCHEMA
// ============================================

export const InvoiceStatus = {
  DRAFT: "draft", // Taslak
  SENT: "sent", // Gönderildi
  PAID: "paid", // Ödendi
  PARTIAL: "partial", // Kısmi ödeme
  OVERDUE: "overdue", // Vadesi geçti
  CANCELLED: "cancelled", // İptal
} as const;

// Kurumsal faturalar - Otomatik fatura destekli
export const invoiceLineItemSchema = z.object({
  washId: z.string(),
  date: z.number(),
  plateNumber: z.string(),
  vehicleType: z.string(),
  serviceName: z.string(),
  serviceId: z.string(),
  unitPrice: z.number(),
  netPrice: z.number(),
});

export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;

export const corporateInvoiceSchema = z.object({
  id: z.string(),
  invoiceNumber: z.string(),
  companyId: z.string(),
  companyName: z.string().optional(),
  
  periodMonth: z.number().min(1).max(12).optional(),
  periodYear: z.number().min(2020).optional(),
  
  issueDate: z.number(),
  dueDate: z.number(),
  
  subtotal: z.number().min(0).optional(),
  discountRate: z.number().min(0).max(100).optional(),
  discountAmount: z.number().min(0).optional(),
  totalAmount: z.number().min(0),
  paidAmount: z.number().min(0).default(0),
  
  washIds: z.array(z.string()).optional(),
  lineItems: z.array(invoiceLineItemSchema).optional(),
  
  fileUrl: z.string().nullable().optional(),
  description: z.string().optional(),
  
  invoiceType: z.enum(["manual", "auto"]).default("manual"),
  generatedAt: z.number().optional(),
  generatedBy: z.string().optional(),
  
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCorporateInvoiceSchema = corporateInvoiceSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CorporateInvoice = z.infer<typeof corporateInvoiceSchema>;
export type InsertCorporateInvoice = z.infer<typeof insertCorporateInvoiceSchema>;

// ============================================
// CUSTOMER ANALYTICS SCHEMA
// ============================================

export const customerAnalyticsSchema = z.object({
  id: z.string(), // customerId ile aynı
  customerId: z.string(),
  
  // Temel metrikler
  totalSpent: z.number().default(0), // Toplam harcama
  visitCount: z.number().default(0), // Toplam ziyaret sayısı
  lastVisitDate: z.number().nullable(), // Son ziyaret tarihi (timestamp)
  firstVisitDate: z.number().nullable(), // İlk ziyaret tarihi
  
  // Hizmet tercihleri (en çok kullanılan 5 hizmet ID'si)
  favoriteServiceIds: z.array(z.string()).default([]),
  
  // Ortalama işlem tutarı
  averageTicketSize: z.number().default(0),
  
  // Son 12 ay harcama trend (aylık)
  monthlySpendHistory: z.array(z.object({
    month: z.string(), // YYYY-MM formatında
    amount: z.number(),
  })).default([]),
  
  // Araç sayısı
  vehicleCount: z.number().default(0),
  
  // Notlar (özel durumlar için)
  notes: z.string().optional(),
  
  updatedAt: z.number(),
  createdAt: z.number(),
});

export const insertCustomerAnalyticsSchema = customerAnalyticsSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CustomerAnalytics = z.infer<typeof customerAnalyticsSchema>;
export type InsertCustomerAnalytics = z.infer<typeof insertCustomerAnalyticsSchema>;

// ============================================
// CORPORATE AGREEMENT SCHEMA
// ============================================

export const AgreementStatus = {
  ACTIVE: "active", // Aktif
  EXPIRED: "expired", // Süresi doldu
  CANCELLED: "cancelled", // İptal edildi
  PENDING: "pending", // Onay bekliyor
} as const;

export const PricingType = {
  DISCOUNT: "discount", // Tüm hizmetlere % indirim
  CUSTOM: "custom", // Hizmet bazlı özel fiyatlar
} as const;

export const corporateAgreementSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  
  // Anlaşma detayları
  agreementNumber: z.string(), // Anlaşma numarası (örn: ANL-2025-001)
  startDate: z.number(), // Başlangıç tarihi (timestamp)
  endDate: z.number(), // Bitiş tarihi (timestamp)
  
  // Fiyatlandırma türü
  pricingType: z.enum([PricingType.DISCOUNT, PricingType.CUSTOM]).default("discount"),
  
  // Fiyatlandırma - İndirim oranı (pricingType: discount için)
  discountRate: z.number().min(0).max(100).default(0), // Tüm hizmetlere uygulanacak indirim oranı (%)
  
  // Fiyatlandırma - Hizmet bazlı özel fiyatlar (pricingType: custom için)
  servicePricing: z.record(z.string(), z.number()).optional().default({}), // { serviceId: customPrice }
  
  // Eski alan (backward compatibility için tutuldu, artık kullanılmıyor)
  monthlyFee: z.number().min(0).optional(), // Deprecated
  
  // Limitler
  vehicleLimit: z.number().nullable().optional(), // Maksimum araç sayısı (null = sınırsız)
  monthlyWashLimit: z.number().nullable().optional(), // Aylık yıkama limiti (null = sınırsız)
  
  // Durum
  status: z.enum([
    AgreementStatus.ACTIVE,
    AgreementStatus.EXPIRED,
    AgreementStatus.CANCELLED,
    AgreementStatus.PENDING,
  ]).default("pending"),
  
  // Döküman
  contractUrl: z.string().optional(), // Anlaşma belgesi URL
  
  // Açıklama ve notlar
  description: z.string().optional(),
  terms: z.string().optional(), // Anlaşma şartları
  notes: z.string().optional(), // Özel notlar
  
  // İletişim
  contactPerson: z.string().optional(), // İletişim kişisi
  contactPhone: z.string().optional(),
  contactEmail: z.string().optional(),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string().optional(), // Admin ID
});

export const insertCorporateAgreementSchema = corporateAgreementSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CorporateAgreement = z.infer<typeof corporateAgreementSchema>;
export type InsertCorporateAgreement = z.infer<typeof insertCorporateAgreementSchema>;

// ============================================
// VEHICLE ANALYTICS SCHEMA
// ============================================

export const vehicleAnalyticsSchema = z.object({
  id: z.string(), // vehicleId ile aynı
  vehicleId: z.string(),
  
  // Temel metrikler
  totalWashCount: z.number().default(0), // Toplam yıkama sayısı
  totalRevenue: z.number().default(0), // Bu araçtan kazanılan toplam gelir
  
  // Tarih bilgileri
  firstWashDate: z.number().nullable(), // İlk yıkama tarihi
  lastWashDate: z.number().nullable(), // Son yıkama tarihi
  
  // Hizmet kullanımı
  serviceUsageCount: z.record(z.string(), z.number()).default({}), // serviceId: count
  mostUsedServiceId: z.string().nullable(),
  
  // Ortalama işlem tutarı
  averageTicketSize: z.number().default(0),
  
  // Aylık kullanım trendi
  monthlyWashHistory: z.array(z.object({
    month: z.string(), // YYYY-MM
    count: z.number(),
    revenue: z.number(),
  })).default([]),
  
  updatedAt: z.number(),
  createdAt: z.number(),
});

export const insertVehicleAnalyticsSchema = vehicleAnalyticsSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type VehicleAnalytics = z.infer<typeof vehicleAnalyticsSchema>;
export type InsertVehicleAnalytics = z.infer<typeof insertVehicleAnalyticsSchema>;

// ============================================
// WEB BUILDER - NAVIGATION SCHEMA
// ============================================

export const NavigationItemType = {
  LINK: "link", // Normal link
  DROPDOWN: "dropdown", // Dropdown menu
  BUTTON: "button", // CTA button
} as const;

export const navigationItemSchema = z.object({
  id: z.string(),
  label: z.string(), // Menü görünen adı
  type: z.enum(["link", "dropdown", "button"]).default("link"),
  url: z.string().optional(), // Link URL (type=link için)
  openInNewTab: z.boolean().default(false),
  order: z.number().default(0),
  active: z.boolean().default(true),
  icon: z.string().optional(), // Lucide icon adı
  
  // Dropdown için
  parentId: z.string().nullable().default(null), // Alt menü için parent ID
  children: z.array(z.string()).default([]), // Child item ID'leri
  
  // Button için
  buttonStyle: z.enum(["primary", "secondary", "outline"]).optional(),
  
  // Görünürlük
  showOnMobile: z.boolean().default(true),
  showOnDesktop: z.boolean().default(true),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string().optional(),
});

export const insertNavigationItemSchema = navigationItemSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type NavigationItem = z.infer<typeof navigationItemSchema>;
export type InsertNavigationItem = z.infer<typeof insertNavigationItemSchema>;

// ============================================
// WEB BUILDER - CUSTOM PAGES SCHEMA
// ============================================

export const PageStatus = {
  DRAFT: "draft", // Taslak
  PUBLISHED: "published", // Yayında
  ARCHIVED: "archived", // Arşivlendi
} as const;

export const PageTemplate = {
  BLANK: "blank", // Boş sayfa
  ABOUT: "about", // Hakkımızda şablonu
  SERVICES: "services", // Hizmetler şablonu
  CONTACT: "contact", // İletişim şablonu
  PRICING: "pricing", // Fiyatlandırma şablonu
  TEAM: "team", // Ekip şablonu
  PORTFOLIO: "portfolio", // Portfolyo şablonu
  CUSTOM: "custom", // Özel
} as const;

export const customPageSchema = z.object({
  id: z.string(),
  title: z.string(), // Sayfa başlığı
  slug: z.string(), // URL slug (örn: about-us)
  template: z.enum(["blank", "about", "services", "contact", "pricing", "team", "portfolio", "custom"]).default("blank"),
  
  // SEO
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  ogImage: z.string().optional(),
  
  // İçerik
  sections: z.array(z.any()).default([]), // Section component'leri
  customCSS: z.string().optional(),
  customJS: z.string().optional(),
  
  // Durum
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  publishedAt: z.number().nullable().optional(),
  
  // Navbar/Footer
  showInNavbar: z.boolean().default(true),
  showInFooter: z.boolean().default(false),
  navbarOrder: z.number().default(999),
  
  // Görünürlük
  isHomepage: z.boolean().default(false),
  requiresAuth: z.boolean().default(false),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});

export const insertCustomPageSchema = customPageSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CustomPage = z.infer<typeof customPageSchema>;
export type InsertCustomPage = z.infer<typeof insertCustomPageSchema>;

// ============================================
// WEB BUILDER - PAGE SECTIONS SCHEMA
// ============================================

export const SectionType = {
  HERO: "hero",
  FEATURES: "features",
  SERVICES: "services",
  STATS: "stats",
  TESTIMONIALS: "testimonials",
  CTA: "cta",
  PRICING: "pricing",
  TEAM: "team",
  GALLERY: "gallery",
  BLOG: "blog",
  FAQ: "faq",
  CONTACT: "contact",
  CUSTOM_HTML: "custom_html",
  FORM: "form",
} as const;

export const pageSectionSchema = z.object({
  id: z.string(),
  pageId: z.string(), // Hangi sayfaya ait
  type: z.enum(["hero", "features", "services", "stats", "testimonials", "cta", "pricing", "team", "gallery", "blog", "faq", "contact", "custom_html", "form"]),
  
  // Pozisyon
  order: z.number().default(0),
  enabled: z.boolean().default(true),
  
  // İçerik (type'a göre değişir)
  content: z.record(z.any()).default({}),
  
  // Stil
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  padding: z.string().optional(), // Örn: "large", "medium", "small"
  
  // Görünürlük
  showOnMobile: z.boolean().default(true),
  showOnDesktop: z.boolean().default(true),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedBy: z.string().optional(),
});

export const insertPageSectionSchema = pageSectionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type PageSection = z.infer<typeof pageSectionSchema>;
export type InsertPageSection = z.infer<typeof insertPageSectionSchema>;

// ============================================
// WEB BUILDER - TEMPLATES SCHEMA
// ============================================

export const siteTemplateSchema = z.object({
  id: z.string(),
  name: z.string(), // Template adı
  description: z.string().optional(),
  thumbnail: z.string().optional(), // Önizleme resmi
  
  // Template içeriği
  pages: z.array(z.any()).default([]), // Sayfa tanımları
  navigation: z.array(z.any()).default([]), // Menü yapısı
  brandSettings: z.any().optional(), // Renk, logo vb.
  
  // Kategori
  category: z.enum(["business", "portfolio", "blog", "ecommerce", "landing", "other"]).default("business"),
  tags: z.array(z.string()).default([]),
  
  // Durum
  isPublic: z.boolean().default(true),
  featured: z.boolean().default(false),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string().optional(),
});

export const insertSiteTemplateSchema = siteTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type SiteTemplate = z.infer<typeof siteTemplateSchema>;
export type InsertSiteTemplate = z.infer<typeof insertSiteTemplateSchema>;

// ============================================
// WEB BUILDER - ANALYTICS SETTINGS SCHEMA
// ============================================

export const analyticsSettingsSchema = z.object({
  id: z.string().default("analytics_settings"),
  
  // Google Analytics
  googleAnalyticsId: z.string().optional(), // GA4 Measurement ID (G-XXXXXXXXXX)
  googleTagManagerId: z.string().optional(), // GTM ID (GTM-XXXXXXX)
  
  // Facebook Pixel
  facebookPixelId: z.string().optional(),
  
  // Other analytics
  hotjarId: z.string().optional(),
  mixpanelToken: z.string().optional(),
  
  // Custom scripts
  headerScripts: z.string().optional(), // <head> içine eklenecek script'ler
  footerScripts: z.string().optional(), // </body> öncesine eklenecek script'ler
  
  // Cookie consent
  enableCookieConsent: z.boolean().default(true),
  cookieConsentText: z.string().optional(),
  
  updatedAt: z.number(),
  updatedBy: z.string().optional(),
});

export const insertAnalyticsSettingsSchema = analyticsSettingsSchema.omit({
  id: true,
  updatedAt: true,
});
export type AnalyticsSettings = z.infer<typeof analyticsSettingsSchema>;
export type InsertAnalyticsSettings = z.infer<typeof insertAnalyticsSettingsSchema>;

// ============================================
// WEB BUILDER - FORM BUILDER SCHEMA
// ============================================

export const FormFieldType = {
  TEXT: "text",
  EMAIL: "email",
  PHONE: "phone",
  TEXTAREA: "textarea",
  SELECT: "select",
  CHECKBOX: "checkbox",
  RADIO: "radio",
  DATE: "date",
  FILE: "file",
} as const;

export const formFieldSchema = z.object({
  id: z.string(),
  formId: z.string(),
  type: z.enum(["text", "email", "phone", "textarea", "select", "checkbox", "radio", "date", "file"]),
  
  label: z.string(),
  placeholder: z.string().optional(),
  required: z.boolean().default(false),
  order: z.number().default(0),
  
  // Validation
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  pattern: z.string().optional(), // Regex pattern
  
  // Options (select, radio, checkbox için)
  options: z.array(z.object({
    value: z.string(),
    label: z.string(),
  })).optional(),
  
  // Varsayılan değer
  defaultValue: z.string().optional(),
  
  // Stil
  width: z.enum(["full", "half", "third"]).default("full"),
  
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const customFormSchema = z.object({
  id: z.string(),
  name: z.string(), // Form adı
  title: z.string(), // Form başlığı
  description: z.string().optional(),
  
  // Form ayarları
  submitButtonText: z.string().default("Gönder"),
  successMessage: z.string().default("Formunuz başarıyla gönderildi!"),
  
  // Email notifications
  notifyEmail: z.string().optional(), // Bildirim gönderilecek email
  autoReplyEnabled: z.boolean().default(false),
  autoReplySubject: z.string().optional(),
  autoReplyMessage: z.string().optional(),
  
  // Fields
  fields: z.array(z.any()).default([]),
  
  // Durum
  active: z.boolean().default(true),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string().optional(),
});

export const insertCustomFormSchema = customFormSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type CustomForm = z.infer<typeof customFormSchema>;
export type InsertCustomForm = z.infer<typeof insertCustomFormSchema>;

// ============================================
// SUBSCRIPTION PACKAGE SCHEMA
// ============================================

export const SubscriptionType = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  SIX_MONTHLY: "six_monthly",
  ANNUAL: "annual",
} as const;

export const SubscriptionStatus = {
  PENDING: "pending",
  ACTIVE: "active",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
  PAUSED: "paused",
} as const;

// Package visibility options
export const PackageVisibility = {
  PUBLIC: "public", // Herkese açık
  CORPORATE: "corporate", // Sadece kurumsal firmalara özel
} as const;

// Abonelik paketi şeması (Admin tarafından oluşturulur)
export const subscriptionPackageSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Paket adı zorunludur"),
  description: z.string().optional(),
  type: z.enum([
    SubscriptionType.MONTHLY,
    SubscriptionType.QUARTERLY,
    SubscriptionType.SIX_MONTHLY,
    SubscriptionType.ANNUAL,
  ]),
  
  // Görünürlük ayarları
  visibility: z.enum([PackageVisibility.PUBLIC, PackageVisibility.CORPORATE]).default(PackageVisibility.PUBLIC),
  allowedCompanyIds: z.array(z.string()).default([]), // Boş ise tüm kurumsal firmalara açık
  
  // Hizmet kotaları (LEGACY - backward compatibility için korunuyor)
  exteriorWashCount: z.number().min(0).default(0), // Dış yıkama sayısı (DEPRECATED)
  interiorCleanCount: z.number().min(0).default(0), // İç temizlik sayısı (DEPRECATED)
  detailingCount: z.number().min(0).default(0), // Detaylı bakım sayısı (DEPRECATED)
  extraServices: z.array(z.string()).default([]), // Ekstra hizmet ID'leri (DEPRECATED)
  
  // YENİ: Servis bazlı kotalar (relational system)
  serviceQuotas: z.array(z.object({
    serviceId: z.string().min(1, "Servis ID zorunludur"),
    quantity: z.number().min(1, "Miktar en az 1 olmalıdır"),
  })).default([]).optional(), // Yeni paketler için zorunlu, eski paketler için optional
  
  // Fiyatlandırma
  price: z.number().min(0, "Fiyat 0'dan büyük olmalıdır"),
  discountPercentage: z.number().min(0).max(100).default(0), // İndirim yüzdesi
  
  // Özellikler ve limitler
  features: z.array(z.string()).default([]), // Özellikler listesi
  maxVehicles: z.number().min(1).default(1), // Maksimum araç sayısı
  priorityBooking: z.boolean().default(false), // Öncelikli randevu
  transferable: z.boolean().default(false), // Başkasına devredilebilir mi
  
  // Görsel ve durum
  imageUrl: z.string().optional(),
  badge: z.string().optional(), // "Popüler", "En İyi Değer" gibi
  badgeColor: z.string().optional(), // Badge rengi
  isPopular: z.boolean().default(false),
  isFeatured: z.boolean().default(false),
  active: z.boolean().default(true),
  displayOrder: z.number().default(0),
  
  createdAt: z.number(),
  updatedAt: z.number(),
  createdBy: z.string().optional(), // Admin ID
});

export const insertSubscriptionPackageSchema = subscriptionPackageSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SubscriptionPackage = z.infer<typeof subscriptionPackageSchema>;
export type InsertSubscriptionPackage = z.infer<typeof insertSubscriptionPackageSchema>;

// ============================================
// CUSTOMER SUBSCRIPTION SCHEMA
// ============================================

// Müşterinin satın aldığı abonelik
export const customerSubscriptionSchema = z.object({
  id: z.string(),
  packageId: z.string(), // SubscriptionPackage ID
  userId: z.string(), // Müşteri ID
  companyId: z.string().optional(), // Kurumsal müşteri için
  
  // Abonelik durumu
  status: z.enum([
    SubscriptionStatus.PENDING,
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.EXPIRED,
    SubscriptionStatus.CANCELLED,
    SubscriptionStatus.PAUSED,
  ]).default(SubscriptionStatus.PENDING),
  
  // Tarihler
  startDate: z.number(),
  endDate: z.number(),
  renewalDate: z.number().optional(), // Otomatik yenileme tarihi
  cancelledAt: z.number().optional(),
  pausedAt: z.number().optional(),
  
  // Kullanım takibi (LEGACY - backward compatibility için korunuyor)
  remainingExteriorWashes: z.number().default(0), // DEPRECATED
  remainingInteriorCleans: z.number().default(0), // DEPRECATED
  remainingDetailings: z.number().default(0), // DEPRECATED
  usedExteriorWashes: z.number().default(0), // DEPRECATED
  usedInteriorCleans: z.number().default(0), // DEPRECATED
  usedDetailings: z.number().default(0), // DEPRECATED
  
  // YENİ: Servis bazlı kullanım takibi (relational system)
  serviceQuotas: z.array(z.object({
    serviceId: z.string().min(1, "Servis ID zorunludur"),
    remaining: z.number().min(0, "Kalan miktar negatif olamaz"),
    used: z.number().min(0, "Kullanılan miktar negatif olamaz"),
  })).default([]).optional(), // Yeni subscriptionlar için zorunlu, eski subscriptionlar için optional
  
  // Araçlar
  vehicleIds: z.array(z.string()).default([]), // Bu aboneliğe tanımlı araçlar
  
  // Ödeme bilgileri
  purchasePrice: z.number(), // Satın alma fiyatı
  paymentMethod: z.string().optional(),
  paymentStatus: z.enum([PaymentStatus.PENDING, PaymentStatus.PAID]).default(PaymentStatus.PENDING),
  paidAt: z.number().optional(),
  cashierId: z.string().optional(), // Ödemeyi kaydeden kasiyer
  
  // Ayarlar
  autoRenew: z.boolean().default(false),
  notifyBeforeExpiry: z.boolean().default(true),
  notifyDaysBefore: z.number().default(7),
  
  // Notlar
  notes: z.string().optional(),
  
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCustomerSubscriptionSchema = customerSubscriptionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CustomerSubscription = z.infer<typeof customerSubscriptionSchema>;
export type InsertCustomerSubscription = z.infer<typeof insertCustomerSubscriptionSchema>;

// ============================================
// SUBSCRIPTION USAGE LOG SCHEMA
// ============================================

// Abonelik kullanım geçmişi
export const subscriptionUsageSchema = z.object({
  id: z.string(),
  subscriptionId: z.string(), // CustomerSubscription ID
  washId: z.string(), // Wash ID
  userId: z.string(),
  vehicleId: z.string(),
  
  // Kullanılan hizmet tipi
  serviceType: z.enum(["exterior", "interior", "detailing", "extra"]),
  serviceId: z.string(),
  serviceName: z.string(),
  
  // Tarih
  usedAt: z.number(),
  
  createdAt: z.number(),
});

export const insertSubscriptionUsageSchema = subscriptionUsageSchema.omit({
  id: true,
  createdAt: true,
});

export type SubscriptionUsage = z.infer<typeof subscriptionUsageSchema>;
export type InsertSubscriptionUsage = z.infer<typeof insertSubscriptionUsageSchema>;

// ============================================
// CORPORATE BUDGET SCHEMA
// ============================================

// Kurumsal müşteri aylık bütçe yönetimi
export const corporateBudgetSchema = z.object({
  id: z.string(),
  corporateCustomerId: z.string(), // User ID (corporate customer)
  
  // Dönem
  month: z.number().min(1).max(12), // 1-12
  year: z.number(), // 2024, 2025, etc.
  
  // Bütçe bilgileri
  limit: z.number(), // Aylık limit (TRY)
  spent: z.number().default(0), // Mevcut harcama
  
  // Uyarı ayarları
  alertThreshold: z.number().default(80), // Uyarı eşiği (%)
  alertSent: z.boolean().default(false), // Uyarı gönderildi mi?
  
  // Notlar
  notes: z.string().optional(),
  
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const insertCorporateBudgetSchema = corporateBudgetSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CorporateBudget = z.infer<typeof corporateBudgetSchema>;
export type InsertCorporateBudget = z.infer<typeof insertCorporateBudgetSchema>;
